import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlertCircle,
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Eye,
  EyeOff,
  Info,
  LayoutList,
} from 'lucide-react';
import { ViewerToolCluster } from '../GeneralViewer/ViewerToolCluster';
import type { LiveViewerExportHandlers } from '../GeneralViewer/ViewerToolCluster';
import { runWithIndeterminateExportNotification } from '../GeneralViewer/exportProgressNotification';
import {
  downloadBlobFile,
  downloadDicomInstance,
  downloadFromDataUrl,
  downloadFrameJpegFromUrl,
  downloadViewFramesAsJpegZip,
  downloadViewFramesAsPngZip,
  downloadViewReferencedDicomInstances,
} from '../GeneralViewer/viewerExportUtils';
import { message, Spin, Tooltip } from 'antd';
import { messageI18n } from '../../../helpers/i18n/toastI18n';
import {
  view_groupe,
} from '../../../../../../ui/src/components/MeasurementTable/Components/Report/config/planes.config';
import {
  getMeasurementDisplayName,
} from '../../../constants/measurementLabels';
import {
  buildViewMeasurementKeysFromViewDef,
} from '../../../helpers/api/config/config';
import { useFilterOptionsOptional } from '../../../contexts/FilterOptionsContext';
import { DrawingCanvas } from '../../ComponnentsUI/DrawingCanvas/components/DrawingCanvas';
import type {
  ImageInput,
  DrawingCanvasRef,
} from '../../ComponnentsUI/DrawingCanvas/components/DrawingCanvas';
import type { Shape } from '../../ComponnentsUI/DrawingCanvas/types';
import {
  usePaginatedViewSegmentationsReport,
  useGetSingleFrame,
  useDeleteFrameView,
  useUpdateQualityFlag,
  useSaveSegmentations,
  useAssignUserView,
  type GeneralViewerInstance,
  type SegmentationInput,
} from '../../../helpers/api/view_frame/view-frames';
import type { DicomMetadataPanelOpenPayload } from '../GeneralViewer/GeneralViewerDicomMetadataPanel';
import { viewFrameQueryKeys } from '../../../helpers/api/view_frame/view-query-keys';
import { useQueryClient } from '@tanstack/react-query';
import { useUpdateStateReport } from '../../../helpers/api/reports/reports';
import { AxiosResponse } from 'axios';
import { ProceedConfirmationModal } from '../ProceedConfirmationModal/proceed-confirmation-modal';
import {
  rawShapeToShape,
  type RawShapeData,
} from '../../ComponnentsUI/DrawingCanvas/utils/pointsConverter';
import { collectDuplicateCategoryDeleteIds, convertShapesToSegmentations, dedupeSegmentationsForLoad, enrichShapeWithMeasurementOverlay, getSegmentationStructureDedupeKey, groupStructureConflictsBySlot, resolveSegmentationCategoryId, resolveSegmentationLabelsForLoad } from '../../ComponnentsUI/DrawingCanvas/utils/segmentationSaveUtils';
import { buildLiveMeasurementsFromShapes, linkAndSyncDerivedBpdShapes } from '../../ComponnentsUI/DrawingCanvas/utils/ellipseBpdUtils';
import {
  calculateLineLength,
  calculateRectangleArea,
  calculateEllipsePerimeter,
  calculateEllipseArea,
  calculatePolygonPerimeter,
  calculatePolylineLength,
  calculatePolygonArea,
  isFreehandShapeClosed,
} from '../../ComponnentsUI/DrawingCanvas/utils/shapeUtils';
import PixelSpacingModal from '../../ComponnentsUI/PixelSpacing';
import { useCategories } from '../../../contexts/CategoriesContext';
import CollaborationDiscussButton from '../Collaboration/CollaborationDiscussButton';
import { buildViewFrameTarget } from '../Collaboration/collaboration-types';
import { useCollaborationViewerLocation } from '../Collaboration/CollaborationContext';

// Label -> MEAS__* is now backend-driven via CategoriesContext.getMeasurementNameFromLabel / labelToMeasName

const MEAS_TO_REPORT_FIELD: Record<string, string> = {
  // T1
  MEAS__CRL: 'crl_cm',
  MEAS__NT: 'nt_cm',
  // Head biometry
  MEAS__BPD: 'bpd_cm',
  MEAS__HC: 'hc_cm',
  // Brain
  MEAS__LATERAL_VENTRICLE_WIDTH: 'lateral_ventricle_cm',
  MEAS__CHOROID_PLEXUS: 'choroid_plexus_cm',
  MEAS__CSP_WIDTH: 'csp_width_cm',
  MEAS__TCD: 'tcd_cm',
  MEAS__CISTERNA_MAGNA: 'cisterna_magna_cm',
  // Cardiac
  MEAS__CTR: 'ctr',
  MEAS__LV_DIAMETER: 'lv_diameter_cm',
  MEAS__RV_DIAMETER: 'rv_diameter_cm',
  MEAS__IVS_THICKNESS: 'ivs_thickness_cm',
  MEAS__AORTA_DIAMETER: 'aorta_diameter_cm',
  MEAS__PA_DIAMETER: 'pa_diameter_cm',
  // Face
  MEAS__ORBITAL_DIAMETER: 'orbital_diameter_cm',
  MEAS__IOD: 'iod_cm',
  // Abdominal
  MEAS__AC: 'ac_cm',
  MEAS__TRANSVERSAL: 'transversal_cm',
  MEAS__BLADDER_DIAMETER: 'bladder_diameter_cm',
  // Limbs
  MEAS__FL: 'fl_cm',
  MEAS__HL: 'hl_cm',
  MEAS__TIBIA_LENGTH: 'tibia_cm',
  // Amniotic fluid
  MEAS__SDP: 'mvp_cm',
  // Cervix & Placenta
  MEAS__CERVICAL_LENGTH: 'cervical_length_cm',
  MEAS__PLACENTAL_THICKNESS: 'placental_thickness_cm',
};

// Helper function to convert backend label (snake_case) to display name (Title Case)
// Removes "not_visible" or "(not visible)" suffix before converting
function backendLabelToDisplayName(label: string): string {
  // Remove "not_visible" suffix (snake_case from backend) or "(not visible)" (already converted)
  const cleanedLabel = label
    .replace(/_?not_?visible$/gi, '') // Remove trailing _not_visible or not_visible
    .replace(/\s*\(not\s*visible\)\s*/gi, '') // Remove (not visible) if already converted
    .replace(/_+$/, '') // Remove any trailing underscores
    .trim();

  return cleanedLabel
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

function normalizeQualFlagCode(raw: string): string {
  return String(raw ?? '')
    .replace(/^QUAL_FLAG__/, '')
    .replace(
      /_(segment_polygon|box_segment|segment_box|segment|polygon|box)$/i,
      ''
    )
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_');
}

// Helper function to convert backend segmentations to DrawingCanvas shapes format
/** Backend returns geometry_type (UPPERCASE). Accept type for backward compat. */
function getSegGeometryType(seg: {
  geometry_type?: string;
  type?: string;
}): string {
  const v = (seg?.geometry_type ?? seg?.type ?? 'FREEHAND')
    .toString()
    .trim()
    .toUpperCase();
  return v || 'FREEHAND';
}

function convertSegmentationToRawShape(
  segmentation: any,
  frameKey: string
): RawShapeData | null {
  if (!segmentation || !segmentation.points) return null;

  try {
    const geoType = getSegGeometryType(segmentation);
    // 1. Parse points: "x1\y1,x2\y2" -> [x1, y1, x2, y2]
    const coords = segmentation.points
      .split(/[,]|[\\]/g) // Split by comma OR backslash
      .map((coord: string) => parseFloat(coord.trim()))
      .filter((coord: number) => !isNaN(coord));

    if (coords.length < 2) return null;

    let shapeType: 'line' | 'square' | 'circle' | 'freehand' = 'freehand';
    let pointsString = '';

    // 2. Map Backend geometry_type (UPPERCASE) to Canvas Types
    if (geoType === 'ELLIPSE') {
      shapeType = 'circle';
      if (coords.length >= 8) {
        // Decode from 4 rotated axis-tip points: p0/p1 = major axis, p2/p3 = minor axis (general viewer round-trip)
        const p0x = coords[0],
          p0y = coords[1];
        const p1x = coords[2],
          p1y = coords[3];
        const p2x = coords[4],
          p2y = coords[5];
        const centerX = (p0x + p1x) / 2;
        const centerY = (p0y + p1y) / 2;
        const radiusX = Math.sqrt((p0x - centerX) ** 2 + (p0y - centerY) ** 2);
        const radiusY = Math.sqrt((p2x - centerX) ** 2 + (p2y - centerY) ** 2);
        let rotation = Math.atan2(p0y - centerY, p0x - centerX);
        if (rotation > Math.PI / 2) rotation -= Math.PI;
        if (rotation < -Math.PI / 2) rotation += Math.PI;
        const cos = Math.cos(rotation);
        const sin = Math.sin(rotation);
        // Encode center + semi-major/minor axis tips (matches shapeToRawShape / rawShapeToShape)
        pointsString = `${centerX}//${centerY},${centerX + radiusX * cos}//${centerY + radiusX * sin},${centerX - radiusY * sin}//${centerY + radiusY * cos}`;
      } else if (coords.length >= 6) {
        // 3 points (center, semi-major end, semi-minor end) — matches cv2.fitEllipse-style encoding
        pointsString = `${coords[0]}//${coords[1]},${coords[2]}//${coords[3]},${coords[4]}//${coords[5]}`;
      } else {
        pointsString = `${coords[0]}//${coords[1]},${coords[2]}//${coords[3]}`;
      }
    } else if (geoType === 'LINE' || geoType === 'POLYLINE') {
      shapeType = 'line';
      pointsString = `${coords[0]}//${coords[1]},${coords[2]}//${coords[3]}`;
    } else if (
      geoType === 'POLYGON' &&
      (coords.length === 8 || segmentation.label === 'meas_rect')
    ) {
      // 4 corners (8 coords) or label meas_rect: treat as rectangle
      const xCoords = coords.filter((_: number, i: number) => i % 2 === 0);
      const yCoords = coords.filter((_: number, i: number) => i % 2 !== 0);
      const minX = Math.min(...xCoords);
      const maxX = Math.max(...xCoords);
      const minY = Math.min(...yCoords);
      const maxY = Math.max(...yCoords);
      shapeType = 'square';
      pointsString = `${minX}//${minY},${maxX}//${maxY}`;
    } else if (geoType === 'POLYGON') {
      const pairs = [];
      for (let i = 0; i < coords.length; i += 2) {
        pairs.push(`${coords[i]}//${coords[i + 1]}`);
      }
      pointsString = pairs.join(',');
      shapeType = 'freehand';
    } else {
      // FREEHAND
      const pairs = [];
      for (let i = 0; i < coords.length; i += 2) {
        pairs.push(`${coords[i]}//${coords[i + 1]}`);
      }
      pointsString = pairs.join(',');
      shapeType = 'freehand';
    }

    // Backend may send label null for structures (resolved from category_id in _resolve_segmentation_labels)
    const resolvedLabel = segmentation.label ?? null;
    const displayLabel = resolvedLabel
      ? backendLabelToDisplayName(resolvedLabel)
      : segmentation.category_id != null
        ? `Category ${segmentation.category_id}`
        : 'Structure';
    const segmentType = (segmentation.segment_type ??
      (resolvedLabel?.startsWith('MEAS__') ? 'measurement' : 'structure')) as
      | 'structure'
      | 'measurement';

    return {
      id: `seg_${frameKey}_${segmentation.id}`,
      type: shapeType,
      points: pointsString,
      label: displayLabel,
      dbId: segmentation.id,
      backendLabel: resolvedLabel,
      backendType: geoType,
      categoryId: segmentation.category_id ?? undefined,
      segmentType,
      isManual: segmentation.is_manual === true,
    };
  } catch (error) {
    console.error('Error converting segmentation:', error);
    return null;
  }
}

interface ViewDetailsProps {
  view: {
    view_code: string;
    display_name: string;
    view_def_id?: number;
  };
  onViewDataChange?: () => void;
  onBack: () => void;
  onNext: () => void;
  onPrevious: () => void;
  currentIndex: number;
  totalViews: number;
  hasNext: boolean;
  hasPrevious: boolean;
  studyUID: string;
  isTransitioning?: boolean;
  onTransitionComplete?: () => void;
  onProceedAsView?: (frameData: TransformedImage) => void;
  selectedFrameViewIds?: Record<string, string>;
  filteredViews: Array<{
    view_code: string;
    display_name: string;
    view_def_id?: number;
  }>;
  onFrameViewUpdated: (viewName: string, frameViewId: string) => void;
  selectedSeries?: string[];
  appliedFilters?: {
    filterMode: string;
    selectedGroups: string[];
    selectedViews: string[];
    selectedQualityFlags: any[];
    magicFilterActive: boolean;
  };
  /** When set, auto-navigate to the frame with this frame_id after load */
  initialFrameId?: string;
  /** Opens DICOM metadata for the currently selected frame (Views / normal viewer). */
  onOpenDicomMetadata?: (payload: DicomMetadataPanelOpenPayload) => void;
  /** Instances for study-level export (DICOM bulk, report PDF). */
  studyDicomInstancesForExport?: GeneralViewerInstance[];
}

interface SegmentationData {
  id?: number | string; // Database ID for existing segmentations
  label: string;
  /** Backend uses geometry_type (UPPERCASE: ELLIPSE, POLYLINE, LINE, FREEHAND, POLYGON). Use getSegGeometryType(seg) to read. */
  geometry_type?: string;
  /** @deprecated Use geometry_type. Kept for backward compat when reading from API. */
  type?: string;
  points: string;
  area?: number;
  perimeter?: number;
  length?: number;
  score?: number;
  sop_instance_uid?: string;
  category_id?: number | null;
  segment_type?: 'structure' | 'measurement';
  display_name?: string;
  flag_def_id?: number | null;
  /** True = drawn by user; False = generated by ML. */
  is_manual?: boolean;
}

interface QualityFlagData {
  id?: string;
  flag_def_id?: number;
  flag_code: string;
  display_name: string;
  default_on?: boolean;
  value: boolean;
  /** When false, criterion is "not visible" / "not added"; UI appends " (Not Visible)" to display. */
  expected_visible?: boolean;
}

interface FrameData {
  image_base64: string;
  frame_number: number;
  quality_flags: QualityFlagData[];
  measurements: {
    id: string;
    meas_name: string;
    value: number;
    unit: string;
    display_name?: string | null;
  }[];
  segmentations: SegmentationData[];
  sop_instance_uid: string;
  serie_instance_uid: string;
  frame_id: string;
  view_def_id: string;
  /** All user-confirmed view IDs (multi-view). */
  user_view_def_ids?: number[] | null;
  /** AI-predicted view IDs for this frame. */
  view_def_ids?: number[] | null;
  global_pixel_spacing?: number[];
  modality?: string;
  pixel_spacing?: [number, number] | null;
  pixel_spacing_unit?: string | null;
  /** Present on API frames for JPEG export via proxy. */
  image_url?: string;
  format_file?: string;
  serie_type?: string;
}

interface TransformedImage {
  image: string;
  originalImage: string;
  frame_number: number;
  frame_id: string;
  /** Same id as frame_id when API uses legacy name (report/proceed payloads). */
  frame_view_id?: string;
  format_file?: string;
  serie_type?: string;
  quality_flags: QualityFlagData[];
  segmentations: SegmentationData[];
  measurements: {
    id: string;
    meas_name: string;
    value: number;
    unit: string;
    display_name?: string | null;
  }[];
  sop_instance_uid: string;
  serie_instance_uid: string;
  isProcessing?: boolean;
  pixel_spacing?: number[];
  pixel_spacing_unit?: string | null;
  global_pixel_spacing?: number[];
  modality?: string;
  /** All user-confirmed view IDs (multi-view). */
  user_view_def_ids?: number[] | null;
  /** AI-predicted view IDs for this frame. */
  view_def_ids?: number[] | null;
}
const useSavePixelSpacing = () => {
  const mutate = async (payload, options) => {
    try {
      // EXAMPLE: Replace with your actual API call
      // await api.post('/series/calibration', payload);
      console.log('Saving pixel spacing:', payload);
      await new Promise((resolve) => setTimeout(resolve, 1000)); // Simulate network delay
      options.onSuccess?.();
    } catch (error) {
      console.error('Failed to save pixel spacing', error);
      options.onError?.(error);
      throw error; // re-throw to be caught by the caller
    }
  };
  // In a real implementation with react-query, you'd return { mutate, isPending }
  return { mutate, isPending: false };
};
/** Small SVG icon that visually represents a grid with `cols` columns × `rows` rows. */
function GridLayoutIcon({
  cols,
  rows = 2,
  size = 14,
}: {
  cols: number;
  rows?: number;
  size?: number;
}) {
  const gap = 1;
  const cellW = (size - (cols - 1) * gap) / cols;
  const cellH = (size - (rows - 1) * gap) / rows;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} fill="none">
      {Array.from({ length: rows }, (_, row) =>
        Array.from({ length: cols }, (_, col) => (
          <rect
            key={`${row}-${col}`}
            x={col * (cellW + gap)}
            y={row * (cellH + gap)}
            width={cellW}
            height={cellH}
            rx={0.5}
            fill="currentColor"
          />
        ))
      )}
    </svg>
  );
}

const ViewDetails = ({
  onViewDataChange,
  view,
  onBack,
  onNext,
  onPrevious,
  currentIndex,
  totalViews,
  hasNext,
  hasPrevious,
  studyUID,
  isTransitioning = false,
  onTransitionComplete,
  onProceedAsView,
  selectedFrameViewIds = {},
  filteredViews,
  onFrameViewUpdated,
  selectedSeries,
  appliedFilters,
  initialFrameId,
  onOpenDicomMetadata,
  studyDicomInstancesForExport = [],
}: ViewDetailsProps) => {
  const { t } = useTranslation(['ViewDetails', 'GeneralViewer']);
  const [pagination, setPagination] = useState({ pageIndex: 1, pageSize: 5 });
  const [selectedFrame, setSelectedFrame] = useState<TransformedImage | null>(
    null
  );
  const [isViewChanging, setIsViewChanging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [allFrames, setAllFrames] = useState<FrameData[]>([]);
  const [showProceedModal, setShowProceedModal] = useState(false);
  const [showAnnotations, setShowAnnotations] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isEditingFlags, setIsEditingFlags] = useState(false);
  const [editingFlagId, setEditingFlagId] = useState<string | null>(null);
  const [isCalibrationModalOpen, setIsCalibrationModalOpen] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [pendingToggleFlag, setPendingToggleFlag] =
    useState<QualityFlagData | null>(null);
  const [isDeletingStructure, setIsDeletingStructure] = useState(false);
  const [canvasKeyVersion, setCanvasKeyVersion] = useState(0);
  /** Bump after refetch to sync current frame shapes without remounting (smooth update, no flash). */
  const [syncShapesVersion, setSyncShapesVersion] = useState(0);
  const [liveCanvasMeasurements, setLiveCanvasMeasurements] = useState<
    Record<string, { value: number; unit: string }>
  >({});
  const { mutate: savePixelSpacing, isPending: isSavingPixelSpacing } =
    useSavePixelSpacing();

  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [frameViewMode, setFrameViewMode] = useState<'single' | 'grid'>(
    'single'
  );
  const [gridSize, setGridSize] = useState<number>(3);
  const [gridPage, setGridPage] = useState<number>(0);
  const [gridHoverN, setGridHoverN] = useState(0);
  const [showGridPicker, setShowGridPicker] = useState(false);
  const gridPickerRef = useRef<HTMLDivElement | null>(null);

  // Close grid picker on outside click
  useEffect(() => {
    if (!showGridPicker) return;
    const handler = (e: MouseEvent) => {
      if (
        gridPickerRef.current &&
        !gridPickerRef.current.contains(e.target as Node)
      ) {
        setShowGridPicker(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showGridPicker]);

  // State for quality criteria sync warning modal
  const [showSegDeleteWarning, setShowSegDeleteWarning] = useState(false);
  const [pendingFlagToggle, setPendingFlagToggle] =
    useState<QualityFlagData | null>(null);

  // State for "draw segmentation" prompt
  const [segmentationPrompt, setSegmentationPrompt] = useState<{
    show: boolean;
    structureName: string;
    toolType: 'line' | 'circle' | 'freehand';
    label: string;
  } | null>(null);

  // Track pending quality flag updates (to be persisted on save)
  // Map of flagId -> newValue
  const [pendingFlagUpdates, setPendingFlagUpdates] = useState<
    Map<string, boolean>
  >(new Map());

  // Ref to control the DrawingCanvas
  const drawingCanvasRef = React.useRef<DrawingCanvasRef>(null);
  const [hiddenManualCriteria, setHiddenManualCriteria] = useState<Set<number>>(
    new Set()
  );
  const {
    categories,
    categoriesByCode,
    getColorForCategoryId,
    getColorForCode,
    measLabelToCategoryId,
    categoryIdToMeasLabel,
    getMeasurementNameFromLabel,
    labelToMeasName,
    codeById,
    categoryIdsWithMaxTwo,
    measGeometryContext,
    categoryIdToMeasNames,
  } = useCategories();
  const filterOptions = useFilterOptionsOptional();
  const viewMeasKeys = useMemo(() => {
    if (!view?.view_code || !filterOptions?.views?.length) return undefined;
    const codeCandidates = [
      view.view_code,
      view.view_code.startsWith('VIEW__')
        ? view.view_code
        : `VIEW__${view.view_code}`,
    ];
    const viewDef =
      filterOptions.views.find((v) => codeCandidates.includes(v.view_code)) ??
      (view.view_def_id != null
        ? filterOptions.views.find((v) => v.id === view.view_def_id)
        : undefined);
    const keys = buildViewMeasurementKeysFromViewDef(categories, viewDef);
    return keys.size > 0 ? keys : undefined;
  }, [view?.view_code, view?.view_def_id, filterOptions?.views, categories]);
  const queryClient = useQueryClient();

  const toggleCriteriaVisibility = (categoryId: number) => {
    setHiddenManualCriteria((prev) => {
      const next = new Set(prev);
      if (next.has(categoryId)) next.delete(categoryId);
      else next.add(categoryId);
      return next;
    });
  };
  // Prepare images with their initial shapes for DrawingCanvas component
  // Inside ViewDetails component
  const imagesWithShapesForDrawingCanvas = useMemo(() => {
    if (!allFrames || allFrames.length === 0) return [];

    return allFrames.map((frame: any) => {
      // used 'any' to allow frame.segmentations
      const frameKey = `${frame.sop_instance_uid}_${frame.frame_number}`;
      const formattedImage = frame.image_base64.startsWith('data:')
        ? frame.image_base64
        : `data:image/jpeg;base64,${frame.image_base64}`;

      const shapes: any[] = [];

      let pixelSpacing: { x: number; y: number } | undefined;
      if (
        frame.pixel_spacing &&
        Array.isArray(frame.pixel_spacing) &&
        frame.pixel_spacing.length >= 2
      ) {
        pixelSpacing = { x: frame.pixel_spacing[0], y: frame.pixel_spacing[1] };
      }

      // NEW: Use the top-level segmentations array we added to the backend query
      const TOOL_TAG_BASE_NAMES: Record<string, string> = {
        meas_line: 'Line',
        meas_ruler: 'Measurement',
        meas_circle: 'Circle',
        meas_freehand: 'Path',
        meas_rect: 'Rectangle',
      };
      const toolTagCounts: Record<string, number> = {
        Line: 0,
        Measurement: 0,
        Circle: 0,
        Path: 0,
        Rectangle: 0,
      };
      if (frame.segmentations && Array.isArray(frame.segmentations)) {
        const segmentationsForLoad = dedupeSegmentationsForLoad(
          frame.segmentations,
          categoryIdsWithMaxTwo,
          measLabelToCategoryId,
          categoryIdToMeasLabel,
          categories
        );
        // Deduplicate ELLIPSE/POLYLINE: when structure + measurement share same biometry slot & points, skip the structure
        const measKeys = new Set<string>();
        for (const seg of segmentationsForLoad) {
          const geo = (seg.geometry_type ?? '').toUpperCase();
          if (
            (geo === 'ELLIPSE' || geo === 'POLYLINE') &&
            (seg.segment_type === 'measurement' ||
              seg.label?.startsWith('MEAS__'))
          ) {
            const key = getSegmentationStructureDedupeKey(
              seg,
              measLabelToCategoryId,
              categoryIdToMeasLabel
            );
            if (key) measKeys.add(key);
          }
        }
        segmentationsForLoad
          .filter((seg: any) => {
            const geo = (seg.geometry_type ?? '').toUpperCase();
            if (
              (geo === 'ELLIPSE' || geo === 'POLYLINE') &&
              seg.segment_type !== 'measurement' &&
              !seg.label?.startsWith('MEAS__')
            ) {
              const key = getSegmentationStructureDedupeKey(
                seg,
                measLabelToCategoryId,
                categoryIdToMeasLabel
              );
              if (key && measKeys.has(key)) {
                return false;
              }
            }
            return true;
          })
          .forEach((seg: any) => {
            const rawShape = convertSegmentationToRawShape(seg, frameKey);
            if (rawShape) {
              try {
                const shape = rawShapeToShape(rawShape);

                const matchingFlag = frame.quality_flags?.find(
                  (f: any) =>
                    f.flag_code === seg.label ||
                    (seg.category_id != null &&
                      f.flag_def_id === seg.category_id)
                );

                let displayLabel: string;
                let resolvedBackendLabel: string | null = seg.label ?? null;
                let resolvedSegmentType: 'structure' | 'measurement' =
                  seg.segment_type === 'measurement' ? 'measurement' : 'structure';

                const baseName = TOOL_TAG_BASE_NAMES[seg.label];
                if (seg.display_name) {
                  displayLabel = seg.display_name;
                } else if (baseName) {
                  toolTagCounts[baseName] =
                    (toolTagCounts[baseName] ?? 0) + 1;
                  displayLabel = `${baseName} ${toolTagCounts[baseName]}`;
                } else if (
                  seg.label &&
                  seg.label.startsWith('meas_') &&
                  !(seg.label in TOOL_TAG_BASE_NAMES)
                ) {
                  const withoutPrefix = seg.label
                    .replace(/^meas_/, '')
                    .replace(/_/g, ' ');
                  displayLabel = withoutPrefix.replace(/\b\w/g, (c) =>
                    c.toUpperCase()
                  );
                } else if (
                  seg.label?.startsWith('MEAS__') ||
                  seg.segment_type === 'measurement'
                ) {
                  const resolved = resolveSegmentationLabelsForLoad(
                    seg,
                    categoryIdToMeasLabel,
                    measLabelToCategoryId,
                    matchingFlag?.display_name ?? null
                  );
                  displayLabel = resolved.displayLabel;
                  resolvedBackendLabel = resolved.backendLabel;
                  resolvedSegmentType = resolved.segmentType;
                } else if (
                  seg.label &&
                  !seg.label.startsWith('MEAS__') &&
                  !(seg.label in TOOL_TAG_BASE_NAMES)
                ) {
                  displayLabel = seg.label;
                } else if (matchingFlag?.display_name) {
                  displayLabel = matchingFlag.display_name;
                } else {
                  displayLabel =
                    matchingFlag?.display_name ||
                    backendLabelToDisplayName(seg.label) ||
                    (seg.category_id != null
                      ? `Category ${seg.category_id}`
                      : 'Structure');
                }

                shapes.push(
                  enrichShapeWithMeasurementOverlay(
                    {
                      ...shape,
                      label: displayLabel,
                      color:
                        seg.category_id != null
                          ? getColorForCategoryId(seg.category_id)
                          : getColorForCode(seg.label ?? undefined),
                      dbId: seg.id,
                      flag_def_id: matchingFlag?.flag_def_id || seg.flag_def_id,
                      backendLabel: resolvedBackendLabel,
                      backendType: getSegGeometryType(seg),
                      categoryId:
                        seg.category_id ??
                        resolveSegmentationCategoryId(seg, measLabelToCategoryId),
                      segmentType: resolvedSegmentType,
                      isNew: false,
                      isDirty: false,
                      isManual: seg.is_manual === true,
                    },
                    seg,
                    frame.measurements || [],
                    pixelSpacing,
                    categoryIdToMeasLabel,
                    measLabelToCategoryId,
                    measGeometryContext,
                    frame.pixel_spacing_unit || undefined,
                    categoryIdToMeasNames
                  ) as any
                );
              } catch (error) {
                console.error(
                  'Error converting top-level segmentation to shape:',
                  error
                );
              }
            }
          });
      }

      linkAndSyncDerivedBpdShapes(
        shapes,
        categories,
        frame.measurements || [],
        pixelSpacing,
        frame.pixel_spacing_unit || undefined,
        measLabelToCategoryId
      );

      return {
        id: frameKey,
        name: `Frame ${frame.frame_number}`,
        src: formattedImage,
        shapes: shapes,
        pixelSpacing: pixelSpacing,
        pixelSpacingUnit: frame.pixel_spacing_unit || undefined,
      };
    });
  }, [allFrames, getColorForCategoryId, getColorForCode, categoryIdsWithMaxTwo, measLabelToCategoryId, categoryIdToMeasLabel, categories, measGeometryContext]);

  // Images with initial shapes for DrawingCanvas
  const imagesForDrawingCanvas = useMemo((): ImageInput[] => {
    return imagesWithShapesForDrawingCanvas.map(
      ({ id, name, src, shapes, pixelSpacing, pixelSpacingUnit }) => ({
        id,
        name,
        src,
        initialShapes: shapes, // Pass the pre-converted shapes
        pixelSpacing: pixelSpacing, // Pass pixel spacing if available
        pixel_spacing_unit: pixelSpacingUnit,
      })
    );
  }, [imagesWithShapesForDrawingCanvas]);

  useEffect(() => {
    if (!selectedFrame) {
      setLiveCanvasMeasurements({});
      return;
    }
    const frameKey = `${selectedFrame.sop_instance_uid}_${selectedFrame.frame_number}`;
    const img = imagesWithShapesForDrawingCanvas.find((f) => f.id === frameKey);
    if (!img?.shapes?.length) {
      setLiveCanvasMeasurements({});
      return;
    }
    setLiveCanvasMeasurements(
      buildLiveMeasurementsFromShapes(
        img.shapes as Array<Record<string, unknown>>,
        img.pixelSpacing,
        img.pixelSpacingUnit,
        categories,
        measLabelToCategoryId
      )
    );
  }, [selectedFrame?.frame_id, imagesWithShapesForDrawingCanvas, categories, measLabelToCategoryId]);

  const convertPixelsToCm = (
    valueInPixels: number,
    pixelSpacing?: number[]
  ) => {
    if (pixelSpacing && pixelSpacing.length > 0) {
      return (valueInPixels * pixelSpacing[0]) / 10;
    }

    return null;
  };

  const hasSpacingForFrame = (frame?: TransformedImage) => {
    if (!frame) return false;

    return Array.isArray(frame.pixel_spacing) && frame.pixel_spacing.length > 0;
  };

  const getEllipseMeasurementDisplay = (
    measurement: { id?: string | number; meas_name?: string },
    frame?: TransformedImage
  ) => {
    if (
      !frame ||
      !hasSpacingForFrame(frame) ||
      !Array.isArray(frame.segmentations)
    ) {
      return null;
    }

    const measName = measurement.meas_name;
    const isPerimeterMeas =
      measName != null && measGeometryContext.perimeterMeasLabels.has(measName);

    const matchingSegmentation = frame.segmentations.find((seg) => {
      if (getSegGeometryType(seg) !== 'ELLIPSE') return false;

      const sameId =
        measurement.id != null &&
        seg.id != null &&
        String(seg.id) === String(measurement.id);
      if (sameId) return true;

      const sameMeasurementLabel =
        measName &&
        typeof seg.label === 'string' &&
        seg.label === measName;
      if (sameMeasurementLabel) return true;

      if (!isPerimeterMeas || !measName) return false;

      const catId =
        seg.category_id != null ? Number(seg.category_id) : undefined;
      if (
        catId != null &&
        (categoryIdToMeasNames[catId] ?? []).includes(measName)
      ) {
        return true;
      }

      const hcCategory = categoriesByCode?.fetal_brain;
      if (measName === 'MEAS__HC') {
        const label = String(seg.label ?? '').toLowerCase();
        if (label === 'fetal_brain') return true;
        if (hcCategory != null && catId === hcCategory.id) return true;
      }

      return false;
    });

    if (!matchingSegmentation) {
      return null;
    }

    const rawShape = convertSegmentationToRawShape(
      matchingSegmentation,
      `${frame.sop_instance_uid}_${frame.frame_number}`
    );
    if (!rawShape) {
      return null;
    }

    try {
      const shape = rawShapeToShape(rawShape) as Shape & {
        radiusX?: number;
        radiusY?: number;
      };
      if (
        shape.type !== 'circle' ||
        typeof shape.radiusX !== 'number' ||
        typeof shape.radiusY !== 'number'
      ) {
        return null;
      }

      return calculateEllipsePerimeter(
        shape.radiusX,
        shape.radiusY,
        { x: frame.pixel_spacing![0], y: frame.pixel_spacing![1] },
        frame.pixel_spacing_unit || 'mm'
      );
    } catch {
      return null;
    }
  };

  const getMeasurementDisplay = (
    measurement: {
      id?: string | number;
      meas_name?: string;
      value: number;
      unit: string;
    },
    frame?: TransformedImage
  ) => {
    const normalizedUnit = (measurement.unit || 'px').trim().toLowerCase();

    if (
      measurement.meas_name &&
      measGeometryContext.perimeterMeasLabels.has(measurement.meas_name)
    ) {
      const ellipseValue = getEllipseMeasurementDisplay(measurement, frame);
      if (ellipseValue !== null) {
        return {
          value: ellipseValue,
          unit: 'cm',
          needsCalibration: false,
        };
      }
    }

    if (!hasSpacingForFrame(frame)) {
      return {
        value: measurement.value,
        unit: 'px',
        needsCalibration: true,
      };
    }

    if (normalizedUnit === 'cm') {
      return {
        value: measurement.value,
        unit: 'cm',
        needsCalibration: false,
      };
    }

    if (normalizedUnit === 'mm') {
      return {
        value: measurement.value / 10,
        unit: 'cm',
        needsCalibration: false,
      };
    }

    const ellipseValue = getEllipseMeasurementDisplay(measurement, frame);
    if (ellipseValue !== null) {
      return {
        value: ellipseValue,
        unit: 'cm',
        needsCalibration: false,
      };
    }

    const convertedValue = convertPixelsToCm(
      measurement.value,
      frame?.pixel_spacing
    );

    if (convertedValue !== null) {
      return {
        value: convertedValue,
        unit: 'cm',
        needsCalibration: false,
      };
    }

    return {
      value: measurement.value,
      unit: 'px',
      needsCalibration: true,
    };
  };

  const needsCalibration = useMemo(() => {
    if (!selectedFrame) return false;

    if (
      !selectedFrame.measurements ||
      selectedFrame.measurements.length === 0
    ) {
      return false;
    }

    return !hasSpacingForFrame(selectedFrame);
  }, [selectedFrame]);

  const canChangeCalibration = useMemo(() => {
    if (!selectedFrame) return false;

    // Don't show change calibration button if there are no measurements
    if (
      !selectedFrame.measurements ||
      selectedFrame.measurements.length === 0
    ) {
      return false;
    }

    const isVideoFormat =
      selectedFrame.format_file === 'VIDEO' ||
      selectedFrame.modality === 'VID' ||
      selectedFrame.serie_type === 'REALTIME';

    if (isVideoFormat) {
      return Boolean(
        selectedFrame.pixel_spacing && selectedFrame.pixel_spacing.length > 0
      );
    }

    return false;
  }, [selectedFrame]);
  const transformedImages = useMemo(() => {
    if (!allFrames || allFrames.length === 0) return [];
    return allFrames.map((frame: FrameData) => {
      const formattedImage = frame.image_base64.startsWith('data:')
        ? frame.image_base64
        : `data:image/jpeg;base64,${frame.image_base64}`;
      const fid = frame.frame_id;
      return {
        image: formattedImage,
        originalImage: formattedImage,
        frame_number: frame.frame_number,
        frame_id: fid,
        frame_view_id: fid,
        quality_flags: frame.quality_flags || [],
        measurements: frame.measurements || [],
        segmentations: frame.segmentations || [],
        format_file: (frame as any).format_file,
        sop_instance_uid: frame.sop_instance_uid,
        serie_instance_uid: frame.serie_instance_uid,
        serie_type: (frame as any).serie_type,
        pixel_spacing: frame.pixel_spacing,
        pixel_spacing_unit: frame.pixel_spacing_unit,
        global_pixel_spacing: frame.global_pixel_spacing,
        modality: frame.modality,
        isProcessing: false,
        user_view_def_ids: (frame as any).user_view_def_ids ?? null,
        view_def_ids: (frame.view_def_ids as number[] | null) ?? null,
      };
    });
  }, [allFrames]);

  const qualityFlagCodes = useMemo(() => {
    if (!appliedFilters || !appliedFilters.selectedQualityFlags?.length)
      return undefined;
    return appliedFilters.selectedQualityFlags.map((flag) => ({
      code: flag.code,
      shouldBeVisible: flag.shouldBeVisible,
    }));
  }, [appliedFilters]);

  // ── Single-frame direct fetch (for instant navigation from thumbnail click) ─
  // Fetches just ONE frame by id — no pagination required. Once loaded, inject it
  // into allFrames and select it immediately so the user sees it right away.
  // Use the prop directly (not the ref) so the query re-fires when initialFrameId changes.
  const { data: singleFrameData, isFetching: singleFrameFetching } =
    useGetSingleFrame(initialFrameId ?? null, studyUID, {
      enabled: !!initialFrameId,
    });

  // When the single-frame fetch resolves, inject it and select it immediately.
  useEffect(() => {
    const frame = singleFrameData?.data?.data ?? singleFrameData?.data;
    if (!frame || !initialFrameId) return;
    if (initialFrameAppliedRef.current) return;
    if (String(frame.frame_id) !== String(initialFrameId)) return;

    initialFrameAppliedRef.current = true;
    pendingFrameIdRef.current = undefined;

    // Inject into allFrames and immediately select it
    setAllFrames((prev) => {
      const already = prev.findIndex(
        (f: any) => String(f.frame_id) === String(frame.frame_id)
      );
      const next =
        already !== -1
          ? prev.map((f: any, i: number) => (i === already ? frame : f))
          : [frame, ...prev];

      // Defer selection so state commit happens first
      const idx = already !== -1 ? already : 0;
      setTimeout(() => {
        setSelectedImageIndex(idx);
        setSelectedFrame((cur: any) =>
          cur?.frame_id === frame.frame_id ? cur : buildTransformedFrame(frame)
        );
      }, 0);

      return next;
    });
  }, [singleFrameData, initialFrameId]);

  // ── Paginated frames (rest of the view) ─────────────────────────────────
  const {
    data: instanceReports,
    isLoading: instanceReportsLoading,
    error: instanceReportsError,
    isFetching: instanceReportsFetching,
    refetch: instanceReportsRefetch,
  } = usePaginatedViewSegmentationsReport(
    pagination.pageIndex,
    pagination.pageSize,
    studyUID,
    view?.view_code,
    selectedSeries,
    qualityFlagCodes,
    appliedFilters?.magicFilterActive ? true : undefined
  );

  // ── Cancel in-flight paginated fetches when view changes ─────────────────
  // Stops fetching pages from the old view as soon as the user navigates away.
  // The paginated query key shape is: [page, studyUID, viewName, ...extras]
  const prevViewCodeRef = React.useRef<string | undefined>(undefined);
  useEffect(() => {
    const prev = prevViewCodeRef.current;
    if (prev && prev !== view?.view_code) {
      // Cancel all in-flight queries that include the old view code in their key
      queryClient.cancelQueries({
        predicate: (query) => {
          const key = query.queryKey as any[];
          // Only cancel requests for the *previous* view code.
          // Including `studyUID` here would cancel the new view's requests too.
          return Array.isArray(key) && key.some((k) => k === prev);
        },
      });
    }
    prevViewCodeRef.current = view?.view_code;
  }, [view?.view_code, studyUID, queryClient]);

  const onSuccess = (response: AxiosResponse) => {
    if (response && selectedFrame) {
      const viewName = view.view_code.startsWith('VIEW__')
        ? view.view_code
        : `VIEW__${view.view_code}`;
      onFrameViewUpdated(viewName, selectedFrame.frame_id);
    }
  };
  const onError = (response: AxiosResponse, error: Error) =>
    console.error('Failed to update report:', error);
  const {
    mutateAsync: muttationUpdateReport,
    isPending: isUpdatingReport,
    isError: isUpdateReportError,
  } = useUpdateStateReport(onSuccess, onError);

  const { deleteFrameView } = useDeleteFrameView();
  const { updateQualityFlag, createOrUpdateQualityFlag } =
    useUpdateQualityFlag();
  const { saveSegmentations } = useSaveSegmentations();
  const { assignUserView } = useAssignUserView();
  const [isSavingSegmentations, setIsSavingSegmentations] = useState(false);
  const [isConfirmingView, setIsConfirmingView] = useState(false);

  // Derived: is this frame AI-assigned to the current view?
  const aiAssignedToCurrentView = useMemo(() => {
    if (!selectedFrame?.view_def_ids || !view?.view_def_id) return false;
    return selectedFrame.view_def_ids.includes(view.view_def_id);
  }, [selectedFrame?.view_def_ids, view?.view_def_id]);

  // Derived: which view did the user manually pick?
  const userAssignedViewName = useMemo(() => {
    const primaryId = selectedFrame?.user_view_def_ids?.[0] ?? null;
    if (!primaryId) return null;
    const v = filteredViews.find((fv) => fv.view_def_id === primaryId);
    return v?.display_name ?? `View #${primaryId}`;
  }, [selectedFrame?.user_view_def_ids, filteredViews]);

  const handleSelectUserView = async (viewDefId: number | null) => {
    if (!selectedFrame?.frame_id) return;
    setIsConfirmingView(true);
    try {
      const result = await assignUserView(selectedFrame.frame_id, viewDefId);
      setSelectedFrame((prev) =>
        prev
          ? {
            ...prev,
            user_view_def_ids:
              result?.user_view_def_ids ?? prev.user_view_def_ids,
          }
          : prev
      );
      await instanceReportsRefetch();
    } catch (e) {
      console.error('Failed to update view assignment:', e);
    } finally {
      setIsConfirmingView(false);
    }
  };

  // Detect user-vs-AI conflicts per biometry slot (BPD line ≠ HC ellipse on fetal brain).
  const structureConflicts = useMemo(() => {
    if (!selectedFrame?.segmentations?.length) return [];
    return groupStructureConflictsBySlot(
      selectedFrame.segmentations as SegmentationData[],
      categoryIdToMeasLabel,
      measLabelToCategoryId
    );
  }, [
    selectedFrame?.segmentations,
    categoryIdToMeasLabel,
    measLabelToCategoryId,
  ]);

  // Delete one or more segmentations by database id (conflict resolution).
  const handleDeleteSegmentations = async (segIds: (number | string)[]) => {
    if (!selectedFrame) return;
    const ids = segIds
      .map((id) => (id != null ? String(id) : ''))
      .filter((id) => id !== '' && id !== 'undefined' && id !== 'null');
    if (ids.length === 0) return;
    try {
      await saveSegmentations({
        sop_instance_uid: selectedFrame.sop_instance_uid,
        frame_number: selectedFrame.frame_number,
        frame_id: selectedFrame.frame_id,
        segmentations: [],
        delete_segmentation_ids: ids,
      } as any);
      message.success(
        t('toast.segmentationRemoved', {
          defaultValue: 'Segmentation removed.',
        })
      );
      // Refetch frames so conflict list + canvas update immediately
      const refetchResult = await instanceReportsRefetch();
      const frames = refetchResult?.data?.frames ?? [];
      if (frames.length > 0) {
        setAllFrames(frames);
        const freshFrame = frames.find(
          (f: any) => f.frame_id === selectedFrame.frame_id
        );
        if (freshFrame) setSelectedFrame(freshFrame as any);
      }
      // Also invalidate general viewer cache
      queryClient.invalidateQueries({
        queryKey: viewFrameQueryKeys.generalViewerInstanceFrames(
          studyUID,
          selectedFrame.sop_instance_uid
        ),
      });
    } catch {
      message.error(
        t('toast.failedRemoveSegmentation', {
          defaultValue: 'Failed to remove segmentation.',
        })
      );
    }
  };

  const handleDeleteSegmentation = async (segId: number | string) => {
    await handleDeleteSegmentations([segId]);
  };

  // Keep ref so instanceReports effect can update selectedFrame without stale closure
  const selectedFrameViewIdRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    selectedFrameViewIdRef.current = selectedFrame?.frame_id ?? null;
  }, [selectedFrame?.frame_id]);

  // Ref to track pending frame navigation (set by initialFrameId prop).
  // We store it in a ref so the instanceReports effect always reads the latest value
  // without needing it in its dependency array (avoids extra re-runs on every prop change).
  const initialFrameAppliedRef = React.useRef(false);
  const pendingFrameIdRef = React.useRef<string | undefined>(undefined);
  useEffect(() => {
    pendingFrameIdRef.current = initialFrameId;
    initialFrameAppliedRef.current = false;
  }, [initialFrameId]);

  // Helper: build a TransformedImage from raw frame data (same shape as transformedImages).
  const buildTransformedFrame = (frame: any): TransformedImage => {
    const img = frame.image_base64?.startsWith('data:')
      ? frame.image_base64
      : `data:image/jpeg;base64,${frame.image_base64 || ''}`;
    const fid = frame.frame_id;
    return {
      image: img,
      originalImage: img,
      frame_number: frame.frame_number,
      frame_id: fid,
      frame_view_id: fid,
      quality_flags: frame.quality_flags || [],
      measurements: frame.measurements || [],
      segmentations: frame.segmentations || [],
      format_file: frame.format_file,
      sop_instance_uid: frame.sop_instance_uid,
      serie_instance_uid: frame.serie_instance_uid,
      serie_type: frame.serie_type,
      pixel_spacing: frame.pixel_spacing,
      pixel_spacing_unit: frame.pixel_spacing_unit,
      global_pixel_spacing: frame.global_pixel_spacing,
      modality: frame.modality,
      isProcessing: false,
      user_view_def_ids: frame.user_view_def_ids ?? null,
      view_def_ids: frame.view_def_ids ?? null,
    } as TransformedImage;
  };

  // When refetch completes, merge new frames by frame_id so allFrames and selectedFrame stay fresh.
  // Do NOT remount the canvas here — that is done explicitly in handleSaveSegmentations after a
  // successful save. Remounting on every background refetch would wipe unsaved drawings.
  useEffect(() => {
    if (!instanceReports?.data?.frames?.length) return;
    const newFrames = instanceReports.data.frames;
    const currentViewId = selectedFrameViewIdRef.current;
    const freshForCurrent = currentViewId
      ? newFrames.find((f: any) => f.frame_id === currentViewId)
      : null;

    // Build the merged list synchronously so we can act on it immediately.
    let mergedFrames: any[];
    setAllFrames((prev) => {
      const byViewId = new Map(newFrames.map((f: any) => [f.frame_id, f]));
      const merged = prev.map((f: any) => byViewId.get(f.frame_id) ?? f);
      const added = newFrames.filter(
        (f: any) => !prev.some((p: any) => p.frame_id === f.frame_id)
      );
      mergedFrames = [...merged, ...added];
      return mergedFrames;
    });

    // ── Priority 1: navigate to the frame the user clicked in the thumbnail strip ──
    const pending = pendingFrameIdRef.current;
    if (pending && !initialFrameAppliedRef.current) {
      // Search the incoming batch first (fastest), then fall back to the full merged list.
      const targetInBatch = newFrames.find(
        (f: any) => String(f.frame_id) === String(pending)
      );
      if (targetInBatch) {
        initialFrameAppliedRef.current = true;
        pendingFrameIdRef.current = undefined;
        const tf = buildTransformedFrame(targetInBatch);
        // Compute the index in the accumulated list so selectedImageIndex stays consistent.
        // mergedFrames may not be set yet (React batches setAllFrames), so search in newFrames
        // context — but we need the global index. Use a small timeout to let setAllFrames commit.
        setTimeout(() => {
          setAllFrames((prev) => {
            const idx = prev.findIndex(
              (f: any) => String(f.frame_id) === String(pending)
            );
            if (idx !== -1) {
              setSelectedImageIndex(idx);
              setSelectedFrame((cur: any) =>
                cur?.frame_id === pending ? cur : tf
              );
            }
            return prev; // no change to allFrames
          });
        }, 0);
        return;
      }
      // Not in this batch — if there are more pages the pagination effect will load them.
    }

    // ── Priority 2: keep current selected frame fresh after refetch ──
    if (freshForCurrent) {
      const formattedImage = freshForCurrent.image_base64?.startsWith('data:')
        ? freshForCurrent.image_base64
        : `data:image/jpeg;base64,${freshForCurrent.image_base64 || ''}`;
      const fid = freshForCurrent.frame_id;
      setSelectedFrame({
        image: formattedImage,
        originalImage: formattedImage,
        frame_number: freshForCurrent.frame_number,
        frame_id: fid,
        frame_view_id: fid,
        quality_flags: freshForCurrent.quality_flags || [],
        measurements: freshForCurrent.measurements || [],
        segmentations: freshForCurrent.segmentations || [],
        format_file: (freshForCurrent as any).format_file,
        sop_instance_uid: freshForCurrent.sop_instance_uid,
        serie_instance_uid: freshForCurrent.serie_instance_uid,
        serie_type: (freshForCurrent as any).serie_type,
        pixel_spacing: freshForCurrent.pixel_spacing,
        pixel_spacing_unit: freshForCurrent.pixel_spacing_unit,
        global_pixel_spacing: freshForCurrent.global_pixel_spacing,
        modality: freshForCurrent.modality,
        isProcessing: false,
        user_view_def_ids: (freshForCurrent as any).user_view_def_ids ?? null,
        view_def_ids: (freshForCurrent as any).view_def_ids ?? null,
      });
    }
  }, [instanceReports]);

  // Update selectedFrame when selectedImageIndex changes
  useEffect(() => {
    if (
      transformedImages.length > 0 &&
      selectedImageIndex < transformedImages.length
    ) {
      setSelectedFrame(transformedImages[selectedImageIndex]);
    }
  }, [selectedImageIndex, transformedImages]);

  const hasMoreFrames = instanceReports?.data?.pagination?.hasMore || false;
  const loadingNextPageRef = React.useRef(false);

  useEffect(() => {
    if (isTransitioning) setIsViewChanging(true);
  }, [isTransitioning]);

  useEffect(() => {
    if (!instanceReportsLoading && !instanceReportsFetching && isViewChanging) {
      setIsViewChanging(false);
      onTransitionComplete?.();
    }
  }, [
    instanceReportsLoading,
    instanceReportsFetching,
    isViewChanging,
    onTransitionComplete,
  ]);

  useEffect(() => {
    setPagination({ pageIndex: 1, pageSize: 5 });
    setSelectedFrame(null);
    setError(null);
    setAllFrames([]);
    setSelectedImageIndex(0);
    loadingNextPageRef.current = false;
  }, [view?.view_code, appliedFilters]);

  useEffect(() => {
    if (transformedImages.length > 0 && !selectedFrame) {
      setSelectedFrame(transformedImages[0]);
    }
  }, [transformedImages, selectedFrame]);

  const handleImageClick = (image: TransformedImage) => setSelectedFrame(image);

  const collaborationViewerTarget = useMemo(() => {
    if (!selectedFrame?.frame_id) return null;
    return buildViewFrameTarget(
      view.display_name,
      String(selectedFrame.frame_id),
      selectedFrame.frame_number,
      view.view_code
    );
  }, [selectedFrame?.frame_id, selectedFrame?.frame_number, view.display_name, view.view_code]);

  useCollaborationViewerLocation(collaborationViewerTarget);

  // Keep a stable ref so handleCanvasImageChange never changes reference between renders.
  // DrawingCanvas has onImageChange in its effect deps — an unstable ref causes an infinite loop.
  const transformedImagesRef = useRef(transformedImages);
  transformedImagesRef.current = transformedImages;

  const handleCanvasImageChange = useCallback(
    (imageId: string, imageIndex: number) => {
      setSelectedImageIndex(imageIndex);
      setLiveCanvasMeasurements({});
      const frame = transformedImagesRef.current[imageIndex];
      if (frame) setSelectedFrame(frame);
    },
    []
  ); // stable — reads transformedImages through ref

  const handleCanvasShapesChange = useCallback(
    (canvasShapes: Shape[], imageId: string) => {
      if (!selectedFrame) return;
      const expectedId = `${selectedFrame.sop_instance_uid}_${selectedFrame.frame_number}`;
      if (imageId !== expectedId) return;
      const img = imagesForDrawingCanvas.find((i) => i.id === imageId);
      setLiveCanvasMeasurements(
        buildLiveMeasurementsFromShapes(
          canvasShapes as Array<Record<string, unknown>>,
          img?.pixelSpacing,
          img?.pixel_spacing_unit,
          categories,
          measLabelToCategoryId
        )
      );
    },
    [selectedFrame, imagesForDrawingCanvas, categories, measLabelToCategoryId]
  );

  const loadMoreFrames = () => {
    if (loadingNextPageRef.current || !hasMoreFrames || instanceReportsFetching)
      return;
    loadingNextPageRef.current = true;
    setPagination((prev) => ({ ...prev, pageIndex: prev.pageIndex + 1 }));
  };
  const loadMoreFramesRef = React.useRef(loadMoreFrames);
  loadMoreFramesRef.current = loadMoreFrames;

  useEffect(() => {
    if (!instanceReportsFetching) loadingNextPageRef.current = false;
  }, [instanceReportsFetching]);

  // ── Auto-load more pages if target frame not found yet ───────────────────
  // pendingFrameIdRef is cleared in the instanceReports effect once the frame is found.
  // If it's still set after a page loads and there are more pages, fetch the next one.
  useEffect(() => {
    if (!pendingFrameIdRef.current) return;
    if (initialFrameAppliedRef.current) return;
    if (instanceReportsFetching) return;
    if (hasMoreFrames) {
      loadMoreFramesRef.current();
    }
  }, [instanceReports, instanceReportsFetching, hasMoreFrames]);

  const handleProceedAsView = () => {
    setShowProceedModal(true);
  };

  const handleDeleteFrameView = () => {
    setShowDeleteModal(true);
  };

  const executeDeleteFrameView = async () => {
    if (!selectedFrame) return;

    try {
      await deleteFrameView(selectedFrame.frame_id);

      const indexToDelete = selectedImageIndex;

      const remainingFrames = allFrames.filter(
        (frame) => frame.frame_id !== selectedFrame.frame_id
      );

      if (remainingFrames.length === 0) {
        if (onViewDataChange) await onViewDataChange();
        onBack();
      } else {
        const nextIndex =
          indexToDelete >= remainingFrames.length
            ? remainingFrames.length - 1
            : indexToDelete;

        setAllFrames(remainingFrames);
        setSelectedImageIndex(nextIndex);

        const nextFrameData = remainingFrames[nextIndex];
        setSelectedFrame(
          transformedImages.find(
            (f) => f.frame_id === nextFrameData.frame_id
          ) || null
        );

        setShowDeleteModal(false);
        message.success(t('toast.frameDeletedSuccess'));
        if (instanceReportsRefetch) await instanceReportsRefetch();

        if (onViewDataChange) onViewDataChange();
      }
    } catch (error) {
      console.error('Delete failed:', error);
      message.error(t('toast.deleteFailed'));
    }
  };

  const handleEditFlags = () => {
    setIsEditingFlags(!isEditingFlags);
  };

  // Helper to extract structure name from flag_code (e.g., "heart_not_visible" -> "heart")
  const getStructureNameFromFlagCode = (flagCode: string): string => {
    return flagCode
      .replace(/_not_?visible$/gi, '')
      .replace(/_/g, ' ')
      .toLowerCase()
      .trim();
  };

  // Use expected_visible from API when present; else fall back to parsing display_name (legacy).
  const isNotVisibleFromFlag = (flag: QualityFlagData): boolean => {
    if (typeof flag.expected_visible === 'boolean')
      return !flag.expected_visible;
    const lowerDisplayName = (flag.display_name || '').toLowerCase();
    return (
      lowerDisplayName.includes('not visible') ||
      lowerDisplayName.includes('not_visible')
    );
  };

  // Helper to determine if a flag is a "not visible" type flag (by display name string, for legacy/code that only has string).
  const isNotVisibleFlag = (flagDisplayName: string): boolean => {
    const lowerDisplayName = (flagDisplayName || '').toLowerCase();
    return (
      lowerDisplayName.includes('not visible') ||
      lowerDisplayName.includes('not_visible')
    );
  };

  // Display label: when expected_visible is false, append " (Not Visible)" in the UI.
  const getQualityFlagDisplayName = (flag: QualityFlagData): string => {
    const base = flag.display_name || '';
    const rawCode = flag.flag_code || base;
    const normalized = normalizeQualFlagCode(rawCode);
    const translatedBase = normalized
      ? t(`qualityFlag.${normalized}`, {
        ns: 'GeneralViewer',
        defaultValue: base,
      })
      : base;
    if (typeof flag.expected_visible === 'boolean' && !flag.expected_visible) {
      return t('qualityFlag.notVisibleSuffix', {
        ns: 'GeneralViewer',
        defaultValue: `${translatedBase} Not Visible`,
        label: translatedBase,
      });
    }
    return translatedBase;
  };

  // Base structure name for matching/drawing (no "Not Visible" suffix).
  const getStructureNameFromFlag = (flag: QualityFlagData): string => {
    if (typeof flag.expected_visible === 'boolean' && !flag.expected_visible)
      return flag.display_name || '';
    return (flag.display_name || '')
      .replace(/\s*not\s*visible\s*/gi, '')
      .trim();
  };

  // Helper to determine what value the flag should have when a segmentation is present
  const getFlagValueWhenSegPresent = (
    flagDisplayNameOrFlag: string | QualityFlagData
  ): boolean => {
    if (typeof flagDisplayNameOrFlag === 'object')
      return !isNotVisibleFromFlag(flagDisplayNameOrFlag);
    return !isNotVisibleFlag(flagDisplayNameOrFlag as string);
  };

  // Helper to determine what value the flag should have when a segmentation is absent
  const getFlagValueWhenSegAbsent = (
    flagDisplayNameOrFlag: string | QualityFlagData
  ): boolean => {
    if (typeof flagDisplayNameOrFlag === 'object')
      return isNotVisibleFromFlag(flagDisplayNameOrFlag);
    return isNotVisibleFlag(flagDisplayNameOrFlag as string);
  };

  // Helper to check if a flag value indicates that a segmentation should be present
  const doesFlagIndicateSegPresent = (flag: QualityFlagData): boolean => {
    if (isNotVisibleFromFlag(flag)) {
      // "not_visible" flag: false means visible (seg should be present)
      return !flag.value;
    } else {
      // "presence" flag: true means present (seg should be present)
      return flag.value;
    }
  };

  // Helper to get tool type for a structure
  const getToolTypeForStructure = (
    structureName: string
  ): 'line' | 'circle' | 'freehand' => {
    const lowerName = structureName.toLowerCase();
    // Ellipse-based structures
    if (
      ['abdomen', 'fetal brain', 'head', 'skull'].some((s) =>
        lowerName.includes(s)
      )
    ) {
      return 'circle';
    }
    // Line-based structures
    if (
      ['femur', 'amniotic fluid', 'af pocket'].some((s) =>
        lowerName.includes(s)
      )
    ) {
      return 'line';
    }
    // Default to freehand
    return 'freehand';
  };

  // Handle shape added - do not optimistically check the quality criteria checkbox.
  // Checkbox state comes from the API response only (after load or after save/refetch).
  const handleShapeAdded = (_shape: Shape, _imageId: string) => {
    // No-op: quality flags are driven by server response, not by local draw action.
  };

  // Handle shape deleted - update quality criteria in client state only (persisted on save)
  const handleShapeDeleted = (
    shapeId: string,
    shape: Shape,
    imageId: string
  ) => {
    if (!shape.label || !selectedFrame) return;

    // 1. Normalize the label for matching (e.g., "Fetal Kidneys" -> "fetal kidneys")
    const shapeLabel = shape.label
      .toLowerCase()
      .replace(/\s*not\s*visible\s*/gi, '')
      .replace(/_/g, ' ')
      .trim();

    // 2. Find the matching quality flag
    const matchingFlag = selectedFrame.quality_flags.find((flag) => {
      const structureName =
        getStructureNameFromFlag(flag) ||
        getStructureNameFromFlagCode(flag.flag_code);
      return (
        structureName.toLowerCase() === shapeLabel ||
        flag.flag_code === shapeLabel.replace(/\s+/g, '_')
      );
    });

    if (matchingFlag) {
      // 3. Update Quality Flags List – update segmentations list but keep the flag value unchanged.
      // Deleting a drawn shape does NOT uncheck quality criteria; the user controls that toggle manually.
      const updatedFlags = selectedFrame.quality_flags.map(
        (f): QualityFlagData => {
          if (f.flag_code === matchingFlag.flag_code) {
            const remainingSegs = (f.segmentations || []).filter(
              (seg) => seg.id !== shapeId && seg.id !== (shape as any).dbId
            );
            return {
              ...f,
              segmentations: remainingSegs,
              // value intentionally NOT changed — quality stays checked as the user set it
            };
          }
          return f;
        }
      );

      // 4. Update local component state
      const updatedFrame = { ...selectedFrame, quality_flags: updatedFlags };
      setSelectedFrame(updatedFrame);

      // 5. Update global frames list (allFrames) to keep everything in sync
      setAllFrames((prevFrames) =>
        prevFrames.map((frame) =>
          frame.frame_id === selectedFrame.frame_id
            ? { ...frame, quality_flags: updatedFlags }
            : frame
        )
      );

      // Flag value did not change – no DB update needed for quality flag on shape deletion
    }
  };
  const isStructureDrawn = (flagCode: string) => {
    console.log('Checking if structure is drawn for flagCode:', flagCode);
    console.log('Selected frame segmentations:', selectedFrame);
    if (!selectedFrame?.segmentations) return false;

    // Clean the flag code to match segmentation labels
    const cleanCode = flagCode.replace(/_not_?visible$/gi, '');

    return selectedFrame.segmentations.some(
      (seg) => seg.label === cleanCode || seg.label === flagCode
    );
  };
  const executeFlagToggle = async (flag: QualityFlagData) => {
    if (!selectedFrame) return;
    setIsDeletingStructure(true);
    try {
      const newValue = !flag.value;

      // 1. Update the quality flag itself
      if (flag.id) {
        await updateQualityFlag(flag.id, newValue);
      }

      // 2. Find segmentations to delete
      const cleanCode = flag.flag_code.replace(/_not_?visible$/gi, '');
      const relatedSegs =
        selectedFrame.segmentations?.filter(
          (seg) =>
            seg.label === cleanCode ||
            seg.label === flag.flag_code ||
            (seg.category_id != null &&
              flag.flag_def_id != null &&
              seg.category_id === flag.flag_def_id)
        ) || [];

      // 3. Send the delete command: backend removes segmentations by id; measurements are derived from segmentations so no separate key needed.
      if (relatedSegs.length > 0) {
        const idsToDelete = relatedSegs
          .map((seg) => (seg.id != null ? String(seg.id) : ''))
          .filter((id) => id !== '');

        await saveSegmentations({
          sop_instance_uid: selectedFrame.sop_instance_uid,
          frame_number: selectedFrame.frame_number,
          frame_id: selectedFrame.frame_id,
          segmentations: [],
          delete_segmentation_ids: idsToDelete,
        });
      }

      // UI Updates — delete by both the display name and the flag code, because
      // canvas shapes may carry either label depending on how they were loaded.
      const structureName = getStructureNameFromFlag(flag);
      if (drawingCanvasRef.current) {
        drawingCanvasRef.current.deleteShapeByLabel(structureName);
        // flag_code is the backend label (e.g. "abdomen"); shapes loaded from DB use this
        if (flag.flag_code && flag.flag_code !== structureName) {
          const cleanCode = flag.flag_code.replace(/_not_?visible$/gi, '');
          drawingCanvasRef.current.deleteShapeByLabel(cleanCode);
        }
      }

      message.success(
        t('toast.structureRemoved', {
          defaultValue: `${structureName} removed.`,
          structure: structureName,
        })
      );

      const savedFrameViewId = selectedFrame?.frame_id;
      const refetchResult = await instanceReportsRefetch();
      const frames = refetchResult?.data?.frames ?? [];

      if (frames.length > 0) {
        const updatedFrame = savedFrameViewId
          ? frames.find((f: any) => f.frame_id === savedFrameViewId)
          : undefined;

        // Merge all refetched frames into allFrames so every page gets fresh data
        setAllFrames((prev) => {
          const byViewId = new Map(frames.map((f: any) => [f.frame_id, f]));
          const merged = prev.map((f: any) => byViewId.get(f.frame_id) ?? f);
          const added = frames.filter(
            (f: any) => !prev.some((p: any) => p.frame_id === f.frame_id)
          );
          return [...merged, ...added];
        });

        if (updatedFrame) {
          const formattedImage = updatedFrame.image_base64?.startsWith('data:')
            ? updatedFrame.image_base64
            : `data:image/jpeg;base64,${updatedFrame.image_base64 || ''}`;
          const fid = updatedFrame.frame_id;
          setSelectedFrame({
            image: formattedImage,
            originalImage: formattedImage,
            frame_number: updatedFrame.frame_number,
            frame_id: fid,
            frame_view_id: fid,
            quality_flags: updatedFrame.quality_flags || [],
            measurements: updatedFrame.measurements || [],
            segmentations: updatedFrame.segmentations || [],
            format_file: (updatedFrame as any).format_file,
            sop_instance_uid: updatedFrame.sop_instance_uid,
            serie_instance_uid: updatedFrame.serie_instance_uid,
            serie_type: (updatedFrame as any).serie_type,
            pixel_spacing: updatedFrame.pixel_spacing,
            pixel_spacing_unit: updatedFrame.pixel_spacing_unit,
            global_pixel_spacing: updatedFrame.global_pixel_spacing,
            modality: updatedFrame.modality,
            isProcessing: false,
          });
        }
      }
      // Invalidate General Viewer cache so delete shows there without manual refetch (same as after save)
      queryClient.invalidateQueries({
        queryKey: viewFrameQueryKeys.generalViewerInstanceFrames(
          studyUID,
          selectedFrame.sop_instance_uid
        ),
      });
      // Use syncShapesVersion (not key remount) so useMultiImageShapes forcibly replaces
      // current frame shapes with the fresh initialShapes — even when canvas had more shapes.
      setTimeout(() => setSyncShapesVersion((v) => v + 1), 0);
    } catch (error) {
      message.error(
        t('toast.genericError', { defaultValue: 'An error occurred.' })
      );
    } finally {
      setIsDeletingStructure(false);
      setShowDeleteConfirmModal(false);
    }
  };
  const handleFlagToggle = async (flag: QualityFlagData) => {
    if (!selectedFrame || !isEditingFlags) return;

    const newValue = !flag.value;
    const isNotVisible = isNotVisibleFromFlag(flag);
    const isSwitchingToPresent = isNotVisible
      ? newValue === false
      : newValue === true;

    if (isSwitchingToPresent) {
      const structureName = getStructureNameFromFlag(flag);
      const categoryId =
        flag.flag_def_id ??
        categoriesByCode[flag.flag_code?.replace(/_not_?visible$/gi, '') ?? ''];
      const hasExistingSeg =
        categoryId != null &&
        (selectedFrame?.segmentations ?? []).some(
          (seg) => seg.category_id === categoryId
        );

      if (!hasExistingSeg && drawingCanvasRef.current) {
        const toolToUse = getToolTypeForStructure(structureName);
        drawingCanvasRef.current.selectPresetTool(toolToUse, structureName);
        messageI18n.info('viewer.toolActiveDraw', { structureName });
      }
    } else {
      setPendingToggleFlag(flag);
      setShowDeleteConfirmModal(true);
    }
  };
  // Confirm segmentation deletion from quality criteria
  const confirmSegmentationDelete = async () => {
    if (pendingFlagToggle) {
      await executeFlagToggle(pendingFlagToggle);
    }
    setShowSegDeleteWarning(false);
    setPendingFlagToggle(null);
  };

  // Cancel segmentation deletion
  const cancelSegmentationDelete = () => {
    setShowSegDeleteWarning(false);
    setPendingFlagToggle(null);
  };

  // Handle draw segmentation prompt - auto-select tool
  const handleDrawSegmentation = () => {
    if (segmentationPrompt && drawingCanvasRef.current) {
      drawingCanvasRef.current.selectPresetTool(
        segmentationPrompt.toolType,
        segmentationPrompt.label
      );
    }
    setSegmentationPrompt(null);
  };

  // Dismiss draw segmentation prompt
  const dismissSegmentationPrompt = () => {
    setSegmentationPrompt(null);
  };

  // Helper function to convert DrawingCanvas shapes to backend segmentation format
  // Helper function to convert display name (Title Case) to backend label (snake_case)
  const displayNameToBackendLabel = (displayName: string): string => {
    return displayName.toLowerCase().replace(/\s+/g, '_');
  };

  // const convertShapesToSegmentations = (
  //   shapes: Shape[],
  //   pixelSpacing?: { x: number; y: number },
  //   pixel_spacing_unit?: string,
  // ): SegmentationInput[] => {
  //   return shapes.map((shape: any) => {
  //     let segType = shape.backendType || 'POLYLINE'; // Use original backend type if available, default to POLYLINE
  //     let points = '';
  //     let area: number | undefined;
  //     let perimeter: number | undefined;
  //     let length: number | undefined;

  //     if (shape.type === 'line') {
  //       segType = shape.backendType || 'LINE';
  //      points = `${shape.startPoint.x}\\${shape.startPoint.y},${shape.endPoint.x}\\${shape.endPoint.y}`;
  //       // Calculate line length
  //       length = calculateLineLength(
  //         shape.startPoint,
  //         shape.endPoint,
  //         pixelSpacing
  //       );
  //     } else if (shape.type === 'circle') {
  //       segType = shape.backendType || 'ELLIPSE';
  //       // Store ellipse as bounding box points
  //        const { center, radiusX, radiusY } = shape;
  //       const left = `${center.x - radiusX}\\${center.y}`;
  //       const right = `${center.x + radiusX}\\${center.y}`;
  //       const top = `${center.x}\\${center.y - radiusY}`;
  //       const bottom = `${center.x}\\${center.y + radiusY}`;

  //       // Format: x1\y1,x2\y2,x3\y3,x4\y4
  //       points = `${left},${right},${top},${bottom}`;
  //       // Calculate ellipse measurements
  //       perimeter = calculateEllipsePerimeter(
  //         shape.radiusX,
  //         shape.radiusY,
  //         pixelSpacing,
  //         pixel_spacing_unit
  //       );
  //       area = calculateEllipseArea(shape.radiusX, shape.radiusY, pixelSpacing);
  //     } else if (shape.type === 'square') {
  //       segType = shape.backendType || 'RECTANGLE';
  //       points = `${shape.startPoint.x}\\${shape.startPoint.y},${shape.endPoint.x}\\${shape.endPoint.y}`;
  //       // Calculate rectangle area
  //       area = calculateRectangleArea(
  //         shape.startPoint,
  //         shape.endPoint,
  //         pixelSpacing
  //       );
  //     }   else if (shape.type === 'freehand') {
  //     segType = shape.backendType || 'POLYLINE';

  //     points = shape.points
  //       .map((p: any) => `${p.x}\\${p.y}`)
  //       .join(',');

  //     if (shape.points && shape.points.length >= 2) {
  //       const isClosed = isFreehandShapeClosed(shape.points);
  //       if (isClosed) {
  //         perimeter = calculatePolygonPerimeter(shape.points, pixelSpacing);
  //         area = calculatePolygonArea(shape.points, pixelSpacing);
  //       } else {
  //         length = calculatePolylineLength(shape.points, pixelSpacing);
  //       }
  //     }
  //   }
  //     // Use backend label if available, otherwise convert display label to snake_case
  //     const label =
  //       shape.backendLabel ||
  //       (shape.label ? displayNameToBackendLabel(shape.label) : 'unknown');
  //     console.log('Converting shape to segmentation:', shape)
  //     // Convert dbId to number if it exists (backend expects integer)
  //     const segmentationId =
  //       shape.dbId !== undefined && shape.dbId !== null
  //         ? typeof shape.dbId === 'number'
  //           ? shape.dbId
  //           : parseInt(String(shape.dbId), 10)
  //         : undefined;
  //     let flag_def_id = shape.flag_def_id || undefined;

  //     return {
  //       id: !isNaN(segmentationId as number) ? segmentationId : undefined, // If it has a database ID, include it for updates
  //       flag_def_id: flag_def_id,
  //       label: label,
  //       type: segType,
  //       points: points,
  //       area: area,
  //       perimeter: perimeter,
  //       length: length,
  //       score: shape.score,
  //     };
  //   });
  // };

  // const handleSaveSegmentations = async (
  //   shapes: Shape[],
  //   imageId: string,
  //   imageName: string,
  //   deletedDbIds: (number | string)[] = []
  // ) => {
  //   // Parse the imageId to get sop_instance_uid and frame_number
  //   const lastUnderscoreIndex = imageId.lastIndexOf('_');
  //   if (lastUnderscoreIndex === -1) {
  //     console.error('Invalid imageId format:', imageId);
  //     return;
  //   }
  //   console.log('Saving segmentations for imageId:', shapes);
  //   const sop_instance_uid = imageId.substring(0, lastUnderscoreIndex);
  //   const frame_number = parseInt(
  //     imageId.substring(lastUnderscoreIndex + 1),
  //     10
  //   );

  //   if (isNaN(frame_number)) {
  //     console.error('Invalid frame number in imageId:', imageId);
  //     return;
  //   }

  //   // Get pixel spacing for the current image
  //   const currentImage = imagesForDrawingCanvas.find(
  //     (img) => img.id === imageId
  //   );
  //   const pixelSpacing = currentImage?.pixelSpacing;
  //   const pixel_spacing_unit = currentImage?.pixel_spacing_unit;
  //   // 1. Get ONLY changed shapes for the 'segmentations' array (added or edited)
  //   // Casting to 'any' to avoid TS errors with isNew/isDirty if not fully strictly typed
  //   const changedShapes = shapes.filter(
  //     (s: any) => s.isNew === true || s.isDirty === true
  //   );
  //   // 2. Determine if there are any changes at all (shapes, deletions, or flags)
  //   console.log('Shapes being sent to backend:', changedShapes);

  //   if (
  //     changedShapes.length === 0 &&
  //     deletedDbIds.length === 0 &&
  //     pendingFlagUpdates.size === 0
  //   ) {
  //     message.info('No changes to save.');
  //     return;
  //   }

  //   setIsSavingSegmentations(true);

  //   try {
  //     // 3. Convert ONLY changed shapes for the segmentations payload
  //     const segmentationsToSave = convertShapesToSegmentations(
  //       changedShapes,
  //       pixelSpacing,
  //       pixel_spacing_unit
  //     );

  //     // 4. Convert ALL shapes to calculate measurements (ensures Biometry table stays full)
  //     const allSegmentations = convertShapesToSegmentations(
  //       shapes,
  //       pixelSpacing,
  //       pixel_spacing_unit
  //     );
  //     const measurements: MeasurementInput[] = [];

  //     allSegmentations.forEach((seg) => {
  //       const measName = getMeasurementNameFromLabel(seg.label);
  //       if (measName) {
  //         let value: number | undefined;

  //         // Circumference (HC, AC)
  //         if (measName === 'MEAS__HC' || measName === 'MEAS__AC') {
  //           value = seg.perimeter;
  //         }
  //         // Length (FL, BPD)
  //         else if (measName === 'MEAS__FL' || measName === 'MEAS__BPD') {
  //           value = seg.length ?? seg.perimeter;
  //         }
  //         // Depth (SDP)
  //         else if (measName === 'MEAS__SDP') {
  //           value = seg.length;
  //         }

  //         if (value !== undefined && !isNaN(value)) {
  //           measurements.push({
  //             meas_name: measName,
  //             value: value,
  //           });
  //         }
  //       }
  //     });
  //     const updatedDbIds = changedShapes
  //       .map((s: any) => s.dbId)
  //       .filter((id) => id !== undefined);
  //     // 5. Format deleted IDs
  //     const segmentationIdsToDelete = deletedDbIds
  //       .map((id) => (typeof id === 'string' ? parseInt(id, 10) : id))
  //       .filter((id) => !isNaN(id) && id > 0)
  //       // FIX: Remove IDs that are actually being updated
  //       .filter((id) => !updatedDbIds.includes(id));

  //     // 6. Execute save API call
  //     const result = await saveSegmentations({
  //       sop_instance_uid,
  //       frame_number,
  //       segmentations: segmentationsToSave,
  //       measurements: measurements,
  //       delete_segmentation_ids:
  //         segmentationIdsToDelete.length > 0
  //           ? segmentationIdsToDelete
  //           : undefined,
  //     });

  //     if (result.success) {
  //       //refetch segmentations and measurements a voir
  //             // if (instanceReportsRefetch) await instanceReportsRefetch();

  //       // 7. Persist any quality flags that were toggled during the session
  //       if (pendingFlagUpdates.size > 0) {
  //         const flagUpdates: Promise<any>[] = [];
  //         pendingFlagUpdates.forEach((newValue, flagId) => {
  //           flagUpdates.push(
  //             updateQualityFlag(flagId, newValue).catch((err) =>
  //               console.error(`Failed to update flag ${flagId}:`, err)
  //             )
  //           );
  //         });
  //         await Promise.all(flagUpdates);
  //         setPendingFlagUpdates(new Map());
  //       }

  //       message.success('Changes saved successfully');
  //       drawingCanvasRef.current?.clearDirtyStates();
  //       // 8. Refresh data (This will reset isNew/isDirty to false by reloading from DB)
  //       instanceReportsRefetch();
  //     } else {
  //       message.error(`Failed to save: ${result.error}`);
  //     }
  //   } catch (error) {
  //     console.error('Error saving segmentations:', error);
  //     message.error('An unexpected error occurred while saving.');
  //   } finally {
  //     setIsSavingSegmentations(false);
  //   }
  // };
  /** Convert changed canvas shapes to API segmentations (shared util). */
  const shapesToSegmentations = (shapes: Shape[]) =>
    convertShapesToSegmentations(
      shapes,
      categoryIdToMeasLabel,
      labelToMeasName,
      measLabelToCategoryId,
      categories,
      measGeometryContext
    );

  const handleSaveSegmentations = async (
    shapes: Shape[],
    imageId: string,
    imageName: string,
    deletedDbIds: (number | string)[] = []
  ) => {
    const lastUnderscoreIndex = imageId.lastIndexOf('_');
    if (lastUnderscoreIndex === -1) return;

    const sop_instance_uid = imageId.substring(0, lastUnderscoreIndex);
    const frame_number = parseInt(
      imageId.substring(lastUnderscoreIndex + 1),
      10
    );
    const currentImage = imagesForDrawingCanvas.find(
      (img) => img.id === imageId
    );

    // --- 2. TRACKING SEGMENTATION DELTAS ---
    const changedShapes = shapes.filter((s: any) => s.isNew || s.isDirty);

    // Measurements are derived from segmentations on the backend (label + length/perimeter); we only send segmentations.
    // --- 3. LOGIC GUARD ---
    const hasDrawingChanges =
      changedShapes.length > 0 || deletedDbIds.length > 0;

    if (!hasDrawingChanges && pendingFlagUpdates.size === 0) {
      console.log('⏭️ No deltas detected. Nothing to save.');
      messageI18n.info('viewer.noChangesToSave');
      return;
    }

    // --- 4. CONSTRUCT PAYLOAD (segmentations only; no measurements key) ---
    const payload = {
      sop_instance_uid,
      frame_number,
      frame_id: selectedFrame.frame_id,
      segmentations: shapesToSegmentations(changedShapes),
      delete_segmentation_ids: collectDuplicateCategoryDeleteIds(
        changedShapes,
        selectedFrame?.segmentations,
        deletedDbIds,
        categoryIdsWithMaxTwo,
        categoryIdToMeasLabel,
        measLabelToCategoryId,
        labelToMeasName,
        categories
      ),
    };

    console.log('📤 LOGICAL DELTA PAYLOAD:', payload);

    setIsSavingSegmentations(true);
    try {
      const result = await saveSegmentations(payload);

      if (result.success) {
        // Assign returned ids to shapes so next save updates instead of creating duplicates
        const savedIds = result.saved_ids ?? [];
        const updatedIds = result.updated_ids ?? [];
        if (
          changedShapes.length > 0 &&
          (savedIds.length > 0 || updatedIds.length > 0)
        ) {
          drawingCanvasRef.current?.assignDbIdsAfterSave(
            changedShapes,
            savedIds,
            updatedIds
          );
        }
        drawingCanvasRef.current?.clearDirtyStates();
        message.success(
          t('toast.changesSaved', {
            defaultValue: 'Changes saved successfully',
          })
        );
        // Invalidate general viewer cache for this instance so 2nd+ time switching to general viewer shows updates
        queryClient.invalidateQueries({
          queryKey: viewFrameQueryKeys.generalViewerInstanceFrames(
            studyUID,
            sop_instance_uid
          ),
        });

        if (pendingFlagUpdates.size > 0) {
          await Promise.all(
            Array.from(pendingFlagUpdates).map(([id, val]) =>
              updateQualityFlag(id, val)
            )
          );
          setPendingFlagUpdates(new Map());
        }

        // Capture frame_id and current page before any awaits to avoid stale closure
        const savedFrameViewId = selectedFrame?.frame_id;
        const currentPageIndex = pagination.pageIndex;

        const refetchResult = await instanceReportsRefetch();
        const frames = refetchResult?.data?.frames ?? [];

        if (frames.length > 0) {
          const updatedFrame = savedFrameViewId
            ? frames.find((f: any) => f.frame_id === savedFrameViewId)
            : undefined;

          // Always merge refetched frames into allFrames so the current page has fresh data (including saved segmentations).
          // This ensures the frame we're on is updated even when refetch returns the same page.
          if (currentPageIndex === 1) {
            setAllFrames(frames);
          } else {
            setAllFrames((prev) => {
              const byViewId = new Map(frames.map((f: any) => [f.frame_id, f]));
              const merged = prev.map(
                (f: any) => byViewId.get(f.frame_id) ?? f
              );
              const added = frames.filter(
                (f: any) => !prev.some((p: any) => p.frame_id === f.frame_id)
              );
              return [...merged, ...added];
            });
          }

          // Update selectedFrame so the current frame on screen shows updated segmentations immediately.
          if (updatedFrame) {
            const formattedImage = updatedFrame.image_base64?.startsWith(
              'data:'
            )
              ? updatedFrame.image_base64
              : `data:image/jpeg;base64,${updatedFrame.image_base64 || ''}`;
            const fid = updatedFrame.frame_id;
            setSelectedFrame({
              image: formattedImage,
              originalImage: formattedImage,
              frame_number: updatedFrame.frame_number,
              frame_id: fid,
              frame_view_id: fid,
              quality_flags: updatedFrame.quality_flags || [],
              measurements: updatedFrame.measurements || [],
              segmentations: updatedFrame.segmentations || [],
              format_file: (updatedFrame as any).format_file,
              sop_instance_uid: updatedFrame.sop_instance_uid,
              serie_instance_uid: updatedFrame.serie_instance_uid,
              serie_type: (updatedFrame as any).serie_type,
              pixel_spacing: updatedFrame.pixel_spacing,
              pixel_spacing_unit: updatedFrame.pixel_spacing_unit,
              global_pixel_spacing: updatedFrame.global_pixel_spacing,
              modality: updatedFrame.modality,
              isProcessing: false,
            });
          }
        }

        // Clear deleted IDs only after successful save so next save does not resend them
        drawingCanvasRef.current?.clearDeletedDbIds?.();
        // Defer sync to next tick so React commits allFrames/selectedFrame first; then canvas gets updated images and sync applies
        setTimeout(() => setSyncShapesVersion((v) => v + 1), 0);
      }
    } catch (error) {
      message.error(
        t('toast.failedSaveChanges', { defaultValue: 'Failed to save changes' })
      );
    } finally {
      setIsSavingSegmentations(false);
    }
  };

  const executeProceeedAsView = async () => {
    if (selectedFrame) {
      const payload: any = {
        study_instance_uid: studyUID,
        frame_id: selectedFrame.frame_id,
        frame_view_id: selectedFrame.frame_view_id ?? selectedFrame.frame_id,
        serie_instance_uid: selectedFrame.serie_instance_uid,
        ...(view.view_def_id != null && { view_def_id: view.view_def_id }),
      };

      const allowedMeasKeys = viewMeasKeys;
      if (
        allowedMeasKeys &&
        selectedFrame.measurements &&
        selectedFrame.measurements.length > 0
      ) {
        for (const m of selectedFrame.measurements) {
          if (!allowedMeasKeys.has(m.meas_name)) continue;
          const reportField = MEAS_TO_REPORT_FIELD[m.meas_name];
          if (!reportField) continue;
          const displayData = getMeasurementDisplay(m, selectedFrame);
          if (reportField === 'ctr') {
            payload[reportField] = displayData.value;
          } else if (displayData.unit === 'cm') {
            payload[reportField] = displayData.value;
          }
        }
      }

      // onProceedAsView?.(selectedFrame);
      muttationUpdateReport(payload);
    }
  };

  const isFrameInReport = (frameViewId: string) => {
    const viewName = view.view_code.startsWith('VIEW__')
      ? view.view_code
      : `VIEW__${view.view_code}`;

    return selectedFrameViewIds[viewName] === frameViewId;
  };

  const getGroupColorClass = (group: string) =>
    ({
      CARDIAC: 'bg-red-100 text-red-800 border-red-200',
      FETAL_HEAD_BRAIN: 'bg-blue-100 text-blue-800 border-blue-200',
      SPINAL: 'bg-green-100 text-green-800 border-green-200',
      ABDOMINAL_GENITALIA: 'bg-orange-100 text-orange-800 border-orange-200',
      LIMB: 'bg-purple-100 text-purple-800 border-purple-200',
      FACIAL: 'bg-cyan-100 text-cyan-800 border-cyan-200',
      FEMUR: 'bg-pink-100 text-pink-800 border-pink-200',
      SAGITTAL: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    })[group] || 'bg-gray-100 text-gray-800 border-gray-200';

  const applyPixelSpacingToFrame = (
    sopInstanceUid: string,
    frameIndex: number,
    pixelSpacingValueMm: number
  ) => {
    const pixelSpacingArray: [number, number] = [
      pixelSpacingValueMm,
      pixelSpacingValueMm,
    ];

    setAllFrames((prevFrames) =>
      prevFrames.map((frame) =>
        frame.sop_instance_uid === sopInstanceUid &&
          Number(frame.frame_number) === frameIndex
          ? {
            ...frame,
            pixel_spacing: pixelSpacingArray,
          }
          : frame
      )
    );

    setSelectedFrame((prevFrame) =>
      prevFrame &&
        prevFrame.sop_instance_uid === sopInstanceUid &&
        Number(prevFrame.frame_number) === frameIndex
        ? {
          ...prevFrame,
          pixel_spacing: pixelSpacingArray,
        }
        : prevFrame
    );
  };

  const buildDicomInstanceFromSelectedFrame =
    useCallback((): GeneralViewerInstance | null => {
      const f = selectedFrame;
      if (!f?.sop_instance_uid || !f?.serie_instance_uid) return null;
      const fmt: 'DICOM' | 'VIDEO' =
        f.format_file === 'VIDEO' ? 'VIDEO' : 'DICOM';
      return {
        sop_instance_uid: f.sop_instance_uid,
        serie_instance_uid: f.serie_instance_uid,
        frame_count: Math.max(1, allFrames.length),
        first_frame_image_url: null,
        format_file: fmt,
        modality: f.modality ?? undefined,
      };
    }, [selectedFrame, allFrames.length]);

  const rawFrameForSelected = useMemo(
    () =>
      allFrames.find(
        (f: FrameData) => String(f.frame_id) === String(selectedFrame?.frame_id)
      ),
    [allFrames, selectedFrame?.frame_id]
  );

  const liveExportForToolbar = useMemo((): LiveViewerExportHandlers | null => {
    if (!selectedFrame) return null;
    const inst = buildDicomInstanceFromSelectedFrame();
    if (!inst) return null;
    const viewFrameTotal = Math.max(
      1,
      instanceReports?.data?.pagination?.total ??
      view.frame_count ??
      allFrames.length ??
      1
    );
    const isVideo =
      selectedFrame.format_file === 'VIDEO' ||
      selectedFrame.modality === 'VID' ||
      selectedFrame.serie_type === 'REALTIME';
    return {
      exportCurrentDicom: () => {
        if ((selectedFrame.format_file ?? 'DICOM') !== 'DICOM') {
          message.warning(t('export.notDicom'));
          return;
        }
        void runWithIndeterminateExportNotification(
          t('export.currentDicom'),
          () => downloadDicomInstance(studyUID, inst)
        ).catch(() => {
          message.error(t('export.dicomFailed'));
        });
      },
      exportFrameJpeg: async () => {
        const imageUrl = rawFrameForSelected?.image_url;
        if (imageUrl) {
          try {
            await runWithIndeterminateExportNotification(
              t('export.currentFrameJpeg'),
              () =>
                downloadFrameJpegFromUrl(
                  imageUrl,
                  `frame_${selectedFrame.sop_instance_uid}_${rawFrameForSelected?.frame_number ?? selectedFrame.frame_number}.jpg`
                )
            );
          } catch {
            message.error(t('export.imageFailed'));
          }
          return;
        }
        try {
          await runWithIndeterminateExportNotification(
            t('export.currentFrameJpeg'),
            async () => {
              downloadFromDataUrl(
                selectedFrame.image,
                `frame_${selectedFrame.sop_instance_uid}_${selectedFrame.frame_number}.jpg`
              );
            }
          );
        } catch {
          message.warning(t('export.noImageUrl'));
        }
      },
      exportViewPng: async () => {
        if (isVideo) {
          message.warning(t('export.videoPngHint'));
          return;
        }
        try {
          await runWithIndeterminateExportNotification(
            t('export.currentViewPng'),
            async () => {
              const blob =
                await drawingCanvasRef.current?.exportViewToPngBlob();
              if (!blob) throw new Error('export.pngFailed');
              downloadBlobFile(
                blob,
                `view_${selectedFrame.sop_instance_uid}_${selectedFrame.frame_number}.png`
              );
            }
          );
        } catch {
          message.error(t('export.pngFailed'));
        }
      },
      canExportViewPng: !isVideo,
      viewPngTitle: isVideo ? t('export.videoPngHint') : undefined,
      instanceIsDicom: (selectedFrame.format_file ?? 'DICOM') === 'DICOM',
      exportViewFramesJpeg: () => {
        void downloadViewFramesAsJpegZip(
          studyUID,
          view.view_code,
          viewFrameTotal,
          t
        );
      },
      exportViewFramesPng: () => {
        void downloadViewFramesAsPngZip(
          studyUID,
          view.view_code,
          viewFrameTotal,
          t
        );
      },
      exportViewAllDicom: () => {
        void downloadViewReferencedDicomInstances(
          studyUID,
          view.view_code,
          viewFrameTotal,
          studyDicomInstancesForExport,
          t
        );
      },
      canExportViewFramesZip: viewFrameTotal > 0,
    };
  }, [
    selectedFrame,
    studyUID,
    rawFrameForSelected,
    buildDicomInstanceFromSelectedFrame,
    t,
    instanceReports?.data?.pagination?.total,
    view.view_code,
    view.frame_count,
    allFrames.length,
    studyDicomInstancesForExport,
  ]);

  const measurementRows = useMemo(() => {
    const measurements = Array.isArray(selectedFrame?.measurements)
      ? selectedFrame.measurements
      : [];
    const allowedMeas = viewMeasKeys;
    const rowsByKey = new Map<
      string,
      {
        id?: string;
        meas_name: string;
        display_name?: string | null;
        value?: number;
        unit?: string;
      }
    >();
    const rowOrder: string[] = [];

    const ensureRow = (measName: string) => {
      if (!rowsByKey.has(measName)) {
        rowsByKey.set(measName, {
          meas_name: measName,
          unit: 'cm',
        });
        rowOrder.push(measName);
      }

      return rowsByKey.get(measName)!;
    };

    allowedMeas?.forEach((measKey) => {
      ensureRow(measKey);
    });

    const frameKey = selectedFrame
      ? `${selectedFrame.sop_instance_uid}_${selectedFrame.frame_number}`
      : null;
    const img = frameKey
      ? imagesWithShapesForDrawingCanvas.find((f) => f.id === frameKey)
      : undefined;
    const geometryMeasurements = img?.shapes?.length
      ? buildLiveMeasurementsFromShapes(
          img.shapes as Array<Record<string, unknown>>,
          img.pixelSpacing,
          img.pixelSpacingUnit,
          categories,
          measLabelToCategoryId
        )
      : {};

    measurements.forEach((measurement) => {
      if (
        measurement.meas_name &&
        (measGeometryContext.perimeterMeasLabels.has(measurement.meas_name) ||
          geometryMeasurements[measurement.meas_name])
      ) {
        return;
      }
      const existing = ensureRow(measurement.meas_name);
      rowsByKey.set(measurement.meas_name, {
        ...existing,
        ...measurement,
        unit: measurement.unit ?? existing.unit ?? 'cm',
      });
    });

    const liveMerged = {
      ...liveCanvasMeasurements,
      ...geometryMeasurements,
    };

    Object.entries(liveMerged).forEach(([measName, live]) => {
      const existing = ensureRow(measName);
      rowsByKey.set(measName, {
        ...existing,
        meas_name: measName,
        value: live.value,
        unit: live.unit ?? existing.unit ?? 'cm',
      });
    });

    allowedMeas?.forEach((measName) => {
      if (!measGeometryContext.perimeterMeasLabels.has(measName)) return;
      const row = rowsByKey.get(measName);
      if (row?.value != null) return;
      const geomVal = getEllipseMeasurementDisplay(
        { meas_name: measName },
        selectedFrame ?? undefined
      );
      if (geomVal == null) return;
      rowsByKey.set(measName, {
        ...row!,
        meas_name: measName,
        value: geomVal,
        unit: 'cm',
      });
    });

    return rowOrder
      .map((measName) => rowsByKey.get(measName))
      .filter(
        (
          row
        ): row is {
          id?: string;
          meas_name: string;
          display_name?: string | null;
          value?: number;
          unit?: string;
        } => Boolean(row)
      );
  }, [
    selectedFrame?.measurements,
    selectedFrame?.sop_instance_uid,
    selectedFrame?.frame_number,
    viewMeasKeys,
    liveCanvasMeasurements,
    imagesWithShapesForDrawingCanvas,
    categories,
    measLabelToCategoryId,
    measGeometryContext,
    categoryIdToMeasNames,
    categoriesByCode,
    selectedFrame,
  ]);

  const viewGrouping = view_groupe[view.view_code];
  const isLoading = instanceReportsLoading || isViewChanging;
  // Show loading state only when we have no frames; never use an overlay so frame nav is never covered
  const showLoadingState = isLoading && imagesForDrawingCanvas.length === 0;

  const handleSavePixelSpacing = async (
    spacingValue: number | { pixel_spacing_cm_per_pixel: number }
  ) => {
    if (!selectedFrame) return;

    const pixelSpacingValueCm =
      typeof spacingValue === 'number'
        ? spacingValue
        : spacingValue.pixel_spacing_cm_per_pixel;
    const pixelSpacingValueMm = pixelSpacingValueCm * 10;

    await savePixelSpacing(
      {
        serie_instance_uid: selectedFrame.serie_instance_uid,
        pixel_spacing: pixelSpacingValueCm,
      },
      {
        onSuccess: () => {
          setIsCalibrationModalOpen(false);
          applyPixelSpacingToFrame(
            selectedFrame.sop_instance_uid,
            Number(selectedFrame.frame_number),
            pixelSpacingValueMm
          );
        },
        onError: (err) => {
          console.error('Error saving pixel spacing:', err);
          // The modal will show an error message
        },
      }
    );
  };

  return (
    <div className="h-full flex flex-col bg-slate-100 dark:bg-gray-900 min-h-0">
      <header className="flex-shrink-0 bg-white dark:bg-gray-800 border-b border-slate-200 dark:border-gray-700 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="w-1/3 flex items-center justify-start gap-4">
            <button
              onClick={onBack}
              className="inline-flex items-center gap-1.5 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-medium transition-colors text-sm"
            >
              <ArrowLeft size={16} />
              {t('header.backToGrid', { defaultValue: 'Back to Grid' })}
            </button>
            {hasPrevious && (
              <button
                onClick={onPrevious}
                disabled={isLoading}
                className="hidden md:flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800 transition-colors p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <ChevronLeft size={16} />
              </button>
            )}
          </div>
          <div className="flex-1 text-center px-4">
            <h1 className="text-xl font-bold text-gray-900 dark:text-white truncate">
              {t(`viewTag.${view.view_code}`, {
                ns: 'GeneralViewer',
                defaultValue: view.display_name,
              })}
            </h1>
            {viewGrouping && (
              <div className="flex justify-center gap-2 mt-1">
                <span
                  className={`px-2 py-0.5 rounded-md text-xs font-medium ${getGroupColorClass(viewGrouping.group)}`}
                >
                  {t(`group.${viewGrouping.group}`, {
                    ns: 'MedicalViewsGrid',
                    defaultValue: viewGrouping.group.replace(/_/g, ' '),
                  })}
                </span>
                <span className="px-2 py-0.5 rounded-md text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                  {t(`family.${viewGrouping.family}`, {
                    ns: 'MedicalViewsGrid',
                    defaultValue: viewGrouping.family,
                  })}
                </span>
              </div>
            )}
          </div>
          <div className="w-1/3 flex items-center justify-end gap-4">
            {hasNext && (
              <button
                onClick={onNext}
                disabled={isLoading}
                className="hidden md:flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800 transition-colors p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <ChevronRight size={16} />
              </button>
            )}
            <span className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded-full font-medium">
              {currentIndex + 1} of {totalViews}
            </span>
          </div>
        </div>
      </header>

      <main className="flex-1 min-h-0 p-4 relative">
        <div className={`grid grid-cols-12 gap-4 h-full min-h-[500px]`}>
          <div
            className={`${frameViewMode !== 'single' ? 'col-span-12 lg:col-span-8' : 'col-span-12 lg:col-span-8'} lg:h-full flex flex-col h-96 bg-white dark:bg-gray-800 rounded-lg border border-slate-200 dark:border-gray-700 shadow-sm overflow-hidden`}
          >
            <div className="p-2 border-b border-slate-200 dark:border-gray-700 flex items-center justify-between flex-shrink-0 gap-2">
              <span className="text-sm font-medium text-gray-600 dark:text-gray-300 pl-2 flex items-center gap-1.5">
                Frame {selectedImageIndex + 1} of {allFrames.length}
                {instanceReports?.data?.pagination?.total != null &&
                  allFrames.length < instanceReports.data.pagination.total && (
                    <span className="text-gray-400 dark:text-gray-500 font-normal">
                      ({allFrames.length} loaded)
                    </span>
                  )}
                {hasMoreFrames && !instanceReportsFetching && (
                  <button
                    type="button"
                    onClick={loadMoreFrames}
                    className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                    title="Load next frames now"
                  >
                    Load more
                  </button>
                )}
              </span>
              <div className="flex items-center gap-2 shrink-0 ml-auto">
                {collaborationViewerTarget && (
                  <CollaborationDiscussButton
                    variant="compact"
                    target={collaborationViewerTarget}
                  />
                )}
                {onOpenDicomMetadata && studyUID && (
                  <ViewerToolCluster
                    studyUID={studyUID}
                    variant="light"
                    dicomActive={false}
                    onDicomClick={() => {
                      const inst = buildDicomInstanceFromSelectedFrame();
                      if (!inst || !selectedFrame) return;
                      onOpenDicomMetadata({
                        studyUID,
                        primarySeriesUid: selectedFrame.serie_instance_uid,
                        initialTab: 'image',
                        selectedInstance: inst,
                      });
                    }}
                    dicomDisabled={
                      !selectedFrame?.sop_instance_uid ||
                      !selectedFrame?.serie_instance_uid
                    }
                    dicomInstances={studyDicomInstancesForExport}
                    liveExport={liveExportForToolbar}
                    showStudyExports={false}
                    showReportExports={false}
                    className="shrink-0"
                  />
                )}
                {/* View mode toggle */}
                <div
                  ref={gridPickerRef}
                  className="relative flex items-center gap-0.5 bg-gray-100 dark:bg-gray-700 rounded-lg p-0.5"
                >
                  {/* Single-view button */}
                  <button
                    type="button"
                    onClick={() => {
                      setFrameViewMode('single');
                      setShowGridPicker(false);
                    }}
                    title="Single view"
                    className={`flex items-center justify-center w-6 h-6 rounded-md transition-all ${frameViewMode === 'single' ? 'bg-white dark:bg-gray-600 text-blue-600 shadow-sm' : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'}`}
                  >
                    <LayoutList size={13} />
                  </button>
                  {/* Divider */}
                  <span className="w-px h-4 bg-gray-300 dark:bg-gray-600 mx-0.5" />
                  {/* Grid picker trigger */}
                  <button
                    type="button"
                    onClick={() => setShowGridPicker((v) => !v)}
                    title="Choose grid layout"
                    className={`flex items-center justify-center w-7 h-6 rounded-md transition-all ${frameViewMode === 'grid' ? 'bg-white dark:bg-gray-600 text-blue-600 shadow-sm' : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'}`}
                  >
                    <GridLayoutIcon
                      cols={frameViewMode === 'grid' ? gridSize : 3}
                      rows={frameViewMode === 'grid' ? gridSize : 3}
                      size={13}
                    />
                  </button>

                  {/* ── Grid picker popover — hover NxN selector ── */}
                  {showGridPicker && (
                    <div
                      ref={gridPickerRef}
                      className="absolute top-full right-0 mt-1.5 z-50 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-2xl p-3 w-44"
                    >
                      <p className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 text-center mb-2 tracking-wide uppercase">
                        Grid size
                      </p>
                      <div
                        className="grid gap-[3px]"
                        style={{ gridTemplateColumns: 'repeat(6, 1fr)' }}
                        onMouseLeave={() => setGridHoverN(0)}
                      >
                        {Array.from({ length: 6 }, (_, row) =>
                          Array.from({ length: 6 }, (_, col) => {
                            const n = Math.max(row + 1, col + 1);
                            const activeN =
                              frameViewMode === 'grid' ? gridSize : 0;
                            const highlighted =
                              gridHoverN > 0
                                ? row < gridHoverN && col < gridHoverN
                                : row < activeN && col < activeN;
                            return (
                              <div
                                key={`${row}-${col}`}
                                onMouseEnter={() => setGridHoverN(n)}
                                onClick={() => {
                                  setGridSize(n);
                                  setGridPage(0);
                                  setFrameViewMode('grid');
                                  setShowGridPicker(false);
                                  setGridHoverN(0);
                                }}
                                className={`h-5 rounded-sm cursor-pointer transition-all border ${highlighted ? 'bg-blue-500 border-blue-400' : 'bg-gray-100 dark:bg-gray-700 border-gray-200 dark:border-gray-600 hover:bg-blue-100 dark:hover:bg-blue-900/30'}`}
                              />
                            );
                          })
                        )}
                      </div>
                      <p className="text-[10px] text-gray-400 dark:text-gray-500 text-center mt-2">
                        {gridHoverN > 0
                          ? `${gridHoverN}×${gridHoverN} (${gridHoverN * gridHoverN} frames)`
                          : frameViewMode === 'grid'
                            ? `${gridSize}×${gridSize} (current)`
                            : 'Hover to select'}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
            {/* Canvas wrapper — both grid and canvas are ALWAYS in DOM (CSS display toggle).
                This prevents scroll-position resets when frames load and keeps DrawingCanvas mounted. */}
            <div className="flex-1 min-h-0 relative overflow-hidden">
              {/* ── NxN grid view — fills viewport, wheel/button to navigate pages ── */}
              {frameViewMode === 'grid' &&
                (() => {
                  const perPage = gridSize * gridSize;
                  const totalFrames = imagesForDrawingCanvas.length;
                  const totalPages = Math.max(
                    1,
                    Math.ceil(totalFrames / perPage)
                  );
                  const safePage = Math.min(gridPage, totalPages - 1);
                  const pageImgs = imagesForDrawingCanvas.slice(
                    safePage * perPage,
                    (safePage + 1) * perPage
                  );
                  return (
                    <div
                      className="absolute inset-0 p-1.5"
                      style={{
                        zIndex: 2,
                        background: '#111827', // solid — hides the single view completely
                        display: 'grid',
                        gridTemplateColumns: `repeat(${gridSize}, minmax(0, 1fr))`,
                        gridTemplateRows: `repeat(${gridSize}, minmax(0, 1fr))`,
                        gap: 5,
                      }}
                      onWheel={(e) => {
                        e.preventDefault();
                        if (e.deltaY > 0)
                          setGridPage((p) => Math.min(totalPages - 1, p + 1));
                        else setGridPage((p) => Math.max(0, p - 1));
                      }}
                    >
                      {pageImgs.map((img, pageIdx) => {
                        const idx = safePage * perPage + pageIdx;
                        const isActive = idx === selectedImageIndex;
                        const frame = allFrames[idx];
                        return (
                          <button
                            key={img.id}
                            type="button"
                            onClick={() => {
                              if (frame) setSelectedFrame(frame as any);
                              setSelectedImageIndex(idx);
                              setFrameViewMode('single');
                            }}
                            className={`group relative focus:outline-none overflow-hidden transition-all rounded-lg ${isActive ? 'ring-2 ring-blue-500 ring-offset-1' : 'ring-1 ring-gray-700 hover:ring-blue-300'}`}
                            style={{ backgroundColor: '#1a1f2e', minHeight: 0 }}
                            title={`Frame ${idx + 1}`}
                          >
                            {img.src ? (
                              <img
                                src={img.src}
                                alt={`Frame ${idx + 1}`}
                                className="absolute inset-0 w-full h-full object-contain transition-transform duration-200 group-hover:scale-105"
                              />
                            ) : (
                              <div className="absolute inset-0 flex items-center justify-center">
                                <div className="w-4 h-4 rounded-full border-2 border-blue-200 border-t-blue-500 animate-spin" />
                              </div>
                            )}
                            <span className="absolute top-1 left-1 h-4 min-w-[16px] px-1 rounded text-white text-[8px] font-bold bg-black/60 flex items-center justify-center">
                              {idx + 1}
                            </span>
                            {frame &&
                              (frame.segmentations?.filter(
                                (s: any) => s.is_manual
                              ).length > 0 ||
                                frame.segmentations?.filter(
                                  (s: any) => !s.is_manual
                                ).length > 0) && (
                                <span className="absolute top-1 right-1 flex gap-0.5 items-center">
                                  {frame.segmentations?.filter(
                                    (s: any) => s.is_manual
                                  ).length > 0 && (
                                      <span
                                        className="w-2 h-2 rounded-full bg-blue-400 border border-white/50"
                                        title={t('legend.yourDrawing')}
                                      />
                                    )}
                                  {frame.segmentations?.filter(
                                    (s: any) => !s.is_manual
                                  ).length > 0 && (
                                      <span
                                        className="w-2 h-2 rounded-full bg-purple-400 border border-white/50"
                                        title={t('legend.aiDrawing')}
                                      />
                                    )}
                                </span>
                              )}
                            {isActive && (
                              <div className="absolute inset-0 pointer-events-none bg-blue-500/20 flex items-end justify-center pb-1">
                                <span className="bg-blue-500 text-white text-[8px] font-bold px-1.5 py-0.5 rounded-full">
                                  {t('thumb.active', {
                                    defaultValue: 'Active',
                                  })}
                                </span>
                              </div>
                            )}
                            {!isActive && (
                              <div
                                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity flex items-end justify-center pb-1"
                                style={{
                                  background:
                                    'linear-gradient(to top, rgba(0,0,0,0.5), transparent)',
                                }}
                              >
                                <span className="text-white text-[8px] font-semibold">
                                  {t('thumb.open', { defaultValue: 'Open' })}
                                </span>
                              </div>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  );
                })()}

              {/* ── Single canvas view — always in DOM, CSS-hidden in grid modes ── */}
              <div
                className="absolute inset-0 flex flex-col"
                style={{
                  zIndex: 1,
                  display: frameViewMode !== 'single' ? 'none' : 'flex',
                }}
              >
                {imagesForDrawingCanvas.length > 0 ? (
                  <div className="w-full flex-1 min-h-0 flex flex-col relative">
                    {/* Drawing legend */}
                    <div className="absolute top-2 left-2 z-20 flex flex-col gap-0.5 bg-black/55 backdrop-blur-sm rounded-lg px-2 py-1.5 pointer-events-none select-none">
                      <span className="flex items-center gap-1.5">
                        <svg
                          width="18"
                          height="6"
                          viewBox="0 0 18 6"
                          fill="none"
                        >
                          <line
                            x1="0"
                            y1="3"
                            x2="18"
                            y2="3"
                            stroke="#60a5fa"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                          />
                        </svg>
                        <span className="text-[9px] text-blue-300 font-medium">
                          {t('legend.yourDrawing')}
                        </span>
                      </span>
                      <span className="flex items-center gap-1.5">
                        <svg
                          width="18"
                          height="6"
                          viewBox="0 0 18 6"
                          fill="none"
                        >
                          <line
                            x1="0"
                            y1="3"
                            x2="18"
                            y2="3"
                            stroke="#c084fc"
                            strokeWidth="1.8"
                            strokeLinecap="round"
                            strokeDasharray="4 3"
                          />
                        </svg>
                        <span className="text-[9px] text-purple-300 font-medium">
                          {t('legend.aiDrawing')}
                        </span>
                      </span>
                    </div>
                    {selectedFrame?.sop_instance_uid && (
                      <Tooltip
                        title={
                          <span className="block text-left">
                            <span className="font-medium">Frame:</span>{' '}
                            {selectedFrame.frame_number ?? '-'}
                            <br />
                            <span className="font-medium">
                              SOP Instance UID:
                            </span>
                            <br />
                            <span className="text-xs break-all">
                              {selectedFrame.sop_instance_uid}
                            </span>
                          </span>
                        }
                      >
                        <span
                          className="absolute top-2 right-2 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-black/60 text-white shadow-md border border-white/20 cursor-pointer hover:bg-black/80 hover:border-white/30 transition-colors"
                          aria-label="Frame info"
                        >
                          <Info size={18} strokeWidth={2.5} />
                        </span>
                      </Tooltip>
                    )}
                    <DrawingCanvas
                      key={`canvas-${view.view_code}-${canvasKeyVersion}`}
                      ref={drawingCanvasRef}
                      images={imagesForDrawingCanvas}
                      initialCurrentImageId={
                        selectedFrame
                          ? `${selectedFrame.sop_instance_uid}_${selectedFrame.frame_number}`
                          : undefined
                      }
                      syncShapesVersion={syncShapesVersion}
                      width={400}
                      height={300}
                      showSaveButton={true}
                      onSave={handleSaveSegmentations}
                      onShapeAdded={handleShapeAdded}
                      onShapeDeleted={handleShapeDeleted}
                      onShapesChange={handleCanvasShapesChange}
                      onImageChange={handleCanvasImageChange}
                      viewCode={view.view_code}
                      hiddenCategoryIds={Array.from(hiddenManualCriteria)}
                      categoriesByCode={categoriesByCode}
                      playbackControl={undefined}
                    />
                  </div>
                ) : showLoadingState ? (
                  <div className="flex-1 flex items-center justify-center w-full min-h-[300px]">
                    <div className="animate-spin rounded-full h-8 w-8 border-2 border-slate-200 dark:border-gray-600 border-t-blue-500" />
                  </div>
                ) : (
                  <div className="flex-1 flex items-center justify-center text-gray-500 min-h-[300px]">
                    No frames available.
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="col-span-12 lg:col-span-4 h-full flex flex-col min-h-[400px] bg-white dark:bg-gray-800 rounded-lg border border-slate-200 dark:border-gray-700 shadow-sm">
            {/* Structure conflict panel — shown prominently above quality criteria */}
            {structureConflicts.length > 0 && (
              <div className="border-b border-orange-200 bg-orange-50 dark:bg-orange-950/30 px-3 py-2">
                {/* Header */}
                <div className="flex items-center gap-1.5 mb-2">
                  <AlertCircle size={13} className="text-orange-500 shrink-0" />
                  <p className="text-xs font-semibold text-orange-600 flex-1">
                    {structureConflicts.length} conflict
                    {structureConflicts.length > 1 ? 's' : ''} detected
                    <span className="font-normal text-orange-400 ml-1">
                      — both you and AI drew the same structure
                    </span>
                  </p>
                  <span className="text-[9px] text-gray-400 shrink-0 italic">
                    See canvas legend ↙
                  </span>
                </div>
                <div className="flex flex-col gap-2">
                  {structureConflicts.map(
                    ({ conflictKey, displayName, userSegs, mlSegs }) => {
                      return (
                        <div
                          key={conflictKey}
                          className="rounded-lg border border-orange-100 bg-white dark:bg-gray-900 p-2"
                        >
                          <p className="text-xs font-semibold text-gray-700 dark:text-gray-200 mb-1.5 capitalize">
                            {displayName}
                          </p>
                          {/* Per-seg badges with individual delete */}
                          <div className="flex flex-wrap gap-1 mb-2">
                            {userSegs.map((seg) => (
                              <span
                                key={String(seg.id)}
                                className="inline-flex items-center gap-1 h-5 px-1.5 rounded bg-blue-100 border border-blue-200 text-blue-700 text-[10px] font-medium"
                              >
                                👤 {t('legend.yourDrawing')}
                                <button
                                  type="button"
                                  onClick={() =>
                                    seg.id != null &&
                                    handleDeleteSegmentation(seg.id)
                                  }
                                  className="ml-0.5 text-red-400 hover:text-red-600 font-bold"
                                  title={t('legend.deleteYourDrawing')}
                                >
                                  ×
                                </button>
                              </span>
                            ))}
                            {mlSegs.map((seg) => (
                              <span
                                key={String(seg.id)}
                                className="inline-flex items-center gap-1 h-5 px-1.5 rounded bg-purple-100 border border-purple-200 text-purple-700 text-[10px] font-medium"
                              >
                                🤖 {t('legend.aiDrawing')}
                                <button
                                  type="button"
                                  onClick={() =>
                                    seg.id != null &&
                                    handleDeleteSegmentation(seg.id)
                                  }
                                  className="ml-0.5 text-red-400 hover:text-red-600 font-bold"
                                  title={t('legend.deleteAiDrawing')}
                                >
                                  ×
                                </button>
                              </span>
                            ))}
                          </div>
                          {/* Resolution buttons — all three are equal clickable options */}
                          <div className="flex gap-1.5 flex-wrap">
                            <button
                              type="button"
                              title={t('legend.keepMineOnlyTitle')}
                              className="h-6 px-2.5 rounded border border-blue-200 bg-blue-50 text-blue-700 text-xs font-medium hover:bg-blue-100 transition-colors"
                              onClick={() =>
                                handleDeleteSegmentations(
                                  mlSegs
                                    .map((seg) => seg.id)
                                    .filter((id): id is number | string => id != null)
                                )
                              }
                            >
                              👤 {t('legend.keepMineOnly')}
                            </button>
                            <button
                              type="button"
                              title={t('legend.useAiOnlyTitle')}
                              className="h-6 px-2.5 rounded border border-purple-200 bg-purple-50 text-purple-700 text-xs font-medium hover:bg-purple-100 transition-colors"
                              onClick={() =>
                                handleDeleteSegmentations(
                                  userSegs
                                    .map((seg) => seg.id)
                                    .filter((id): id is number | string => id != null)
                                )
                              }
                            >
                              🤖 {t('legend.useAiOnly')}
                            </button>
                            <button
                              type="button"
                              title={t('legend.keepBothTitle')}
                              className="h-6 px-2.5 rounded border border-gray-200 bg-white dark:bg-gray-800 text-gray-500 text-xs font-medium hover:bg-gray-50 hover:border-gray-300 transition-colors"
                              onClick={() => {
                                /* both already kept — just a visual acknowledgement, no action needed */
                              }}
                            >
                              {t('legend.keepBoth')}
                            </button>
                          </div>
                        </div>
                      );
                    }
                  )}
                </div>
              </div>
            )}

            <div className="p-3 border-b border-slate-200 dark:border-gray-700 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                <h2 className="font-semibold text-gray-800 dark:text-gray-200 text-sm">
                  {t('qualityPanel.title', {
                    defaultValue: 'Quality Criteria',
                  })}
                </h2>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleEditFlags}
                  className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${isEditingFlags
                    ? 'bg-blue-100 text-blue-700 border border-blue-200'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                >
                  {isEditingFlags
                    ? t('qualityPanel.done', { defaultValue: 'Done' })
                    : t('qualityPanel.edit', { defaultValue: 'Edit' })}
                </button>
                <div className="text-orange-500 text-lg">⚡</div>
              </div>
            </div>
            <div className="p-3 border-b border-slate-200 dark:border-gray-700 flex items-center justify-end">
              <div className="flex items-center gap-2">
                <button
                  onClick={handleProceedAsView}
                  disabled={
                    !selectedFrame ||
                    isFrameInReport(selectedFrame.frame_id) ||
                    isUpdatingReport
                  }
                  className={`px-3 py-1.5 rounded-lg font-medium text-xs transition-all duration-200 flex items-center gap-1.5 ${!selectedFrame ||
                    isFrameInReport(selectedFrame.frame_id) ||
                    isUpdatingReport
                    ? 'bg-gray-200 dark:bg-gray-600 cursor-not-allowed text-gray-500 dark:text-gray-400'
                    : isUpdateReportError
                      ? 'bg-red-600 hover:bg-red-700 text-white'
                      : 'bg-blue-600 hover:bg-blue-700 text-white'
                    }`}
                >
                  {isUpdatingReport && <Spin size="small" />}
                  {isUpdateReportError
                    ? t('qualityPanel.retry', { defaultValue: 'Retry' })
                    : isUpdatingReport
                      ? '...'
                      : isFrameInReport(selectedFrame?.frame_id)
                        ? t('qualityPanel.inReport', {
                          defaultValue: 'In Report',
                        })
                        : t('qualityPanel.proceed', {
                          defaultValue: 'Proceed',
                        })}
                </button>
                <button
                  onClick={handleDeleteFrameView}
                  disabled={!selectedFrame}
                  className={`px-2.5 py-1.5 rounded-lg font-medium text-xs transition-all duration-200 ${!selectedFrame
                    ? 'bg-gray-200 dark:bg-gray-600 cursor-not-allowed text-gray-500 dark:text-gray-400'
                    : 'bg-red-100 hover:bg-red-200 text-red-700 hover:text-red-800 border border-red-200 hover:border-red-300'
                    }`}
                  title={t('qualityPanel.deleteFrameTitle', {
                    defaultValue: 'Delete Frame',
                  })}
                >
                  {t('qualityPanel.delete', { defaultValue: 'Delete' })}
                </button>
              </div>
            </div>
            <div className="flex-1 p-4 overflow-y-auto space-y-3">
              {showLoadingState ? (
                <div className="flex items-center justify-center h-full">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : (
                <>
                  {measurementRows.length > 0 && (
                    <div className="border rounded-lg p-3 bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 border-blue-200 dark:border-blue-700">
                      <div className="text-xs font-semibold text-blue-800 dark:text-blue-300 mb-2 uppercase tracking-wide">
                        {t('qualityPanel.measurementsTitle', {
                          defaultValue: 'Measurements',
                        })}
                      </div>
                      <div className="space-y-2">
                        {measurementRows.map((row, idx) => {
                          const displayData =
                            row.value !== undefined && selectedFrame
                              ? getMeasurementDisplay(
                                {
                                  id: row.id,
                                  meas_name: row.meas_name,
                                  value: row.value,
                                  unit: row.unit ?? 'cm',
                                },
                                selectedFrame
                              )
                              : null;
                          const translationKey = `measurements.${row.meas_name.replace('MEAS__', '').toLowerCase()}`;

                          const label = t(translationKey, {
                            defaultValue: row.display_name ?? getMeasurementDisplayName(row.meas_name)
                          });
                          return (
                            <div
                              key={`${row.meas_name}-${idx}`}
                              className="flex justify-between items-center"
                            >
                              <span className="text-sm text-gray-700 dark:text-gray-300">
                                {label}
                              </span>
                              <div className="flex items-center gap-2">
                                {displayData ? (
                                  <>
                                    <span
                                      className={`text-sm font-bold ${displayData.needsCalibration ? 'text-yellow-600 dark:text-yellow-400' : 'text-blue-700 dark:text-blue-400'}`}
                                    >
                                      {displayData.value.toFixed(2)}{' '}
                                      {displayData.unit}
                                    </span>
                                    {displayData.needsCalibration && (
                                      <AlertCircle
                                        size={14}
                                        className="text-yellow-600 dark:text-yellow-400"
                                      />
                                    )}
                                  </>
                                ) : (
                                  <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                    —
                                  </span>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {needsCalibration && (
                    <div className="border rounded-lg p-3 bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-700">
                      <div className="flex items-center gap-2 text-yellow-800 dark:text-yellow-300 mb-2">
                        <AlertCircle size={16} />
                        <h4 className="text-sm font-semibold">
                          {t('calibration.requiredTitle', {
                            defaultValue: 'Calibration Required',
                          })}
                        </h4>
                      </div>
                      <p className="text-sm text-yellow-700 dark:text-yellow-400 mb-3">
                        {t('calibration.requiredBody', {
                          defaultValue:
                            'This frame has measurements in pixels but lacks pixel spacing data. Please calibrate to convert measurements to centimeters.',
                        })}
                      </p>
                      <button
                        onClick={() => setIsCalibrationModalOpen(true)}
                        className="w-full text-center px-4 py-2 bg-yellow-400 hover:bg-yellow-500 text-yellow-900 font-semibold rounded-lg text-sm transition-colors"
                      >
                        {t('calibration.calibrateNow', {
                          defaultValue: 'Calibrate Now',
                        })}
                      </button>
                    </div>
                  )}
                  {canChangeCalibration && !needsCalibration && (
                    <div className="border rounded-lg p-2 bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-700">
                      <button
                        onClick={() => setIsCalibrationModalOpen(true)}
                        className="w-full text-center px-3 py-1.5 text-xs text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-800/30 rounded transition-colors flex items-center justify-center gap-1.5"
                      >
                        <span>⚙️</span>
                        <span>
                          {t('calibration.changeCalibration', {
                            defaultValue: 'Change Calibration',
                          })}
                        </span>
                      </button>
                    </div>
                  )}
                  {selectedFrame?.quality_flags?.length > 0 ? (
                    selectedFrame?.quality_flags?.map((flag, i) => {
                      const isNotVisibleType = isNotVisibleFromFlag(flag);

                      // The Eye (hide/show) functionality:
                      // If it's a "Not Visible" flag and value is TRUE, the structure is absent (EyeOff).
                      // If it's a "Presence" flag and value is FALSE, the structure is absent (EyeOff).
                      const isStructureVisibleInTheory = isNotVisibleType
                        ? !flag.value
                        : flag.value;

                      const categoryId =
                        flag.flag_def_id ?? categoriesByCode[flag.flag_code];
                      const isHidden =
                        categoryId != null &&
                        hiddenManualCriteria.has(categoryId);

                      // Find user/AI segs for this category to show source badges.
                      // Include all segment_types: biometry shapes (FL, AC…) are stored as
                      // 'measurement' but still need 👤/🤖 badges on quality criteria.
                      const segsForCategory =
                        categoryId != null
                          ? (selectedFrame?.segmentations ?? []).filter(
                            (s) => s.category_id === categoryId
                          )
                          : [];
                      const hasUserSeg = segsForCategory.some(
                        (s) => s.is_manual === true
                      );
                      const hasAiSeg = segsForCategory.some(
                        (s) => s.is_manual === false
                      );

                      return (
                        <div
                          key={i}
                          className={`border rounded-xl p-3 flex flex-col gap-1.5 transition-all duration-200 ${isEditingFlags
                              ? 'bg-blue-50/50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-500/30'
                              : 'bg-white dark:bg-slate-900/40 border-slate-200 dark:border-slate-800'
                            }`}
                        >
                          <div className="flex justify-between items-center">
                            <div className="flex items-center gap-3 flex-1">
                              {/* Eye / Visibility Toggle */}
                              <button
                                onClick={() => categoryId != null && toggleCriteriaVisibility(categoryId)}
                                className={`transition-colors ${!isStructureVisibleInTheory
                                    ? 'opacity-20'
                                    : isHidden
                                      ? 'text-slate-400 dark:text-slate-600'
                                      : 'text-blue-500 dark:text-blue-400'
                                  }`}
                              >
                                {isHidden || !isStructureVisibleInTheory ? <EyeOff size={18} /> : <Eye size={18} />}
                              </button>

                              {/* Color Dot */}
                              <div
                                className="w-3 h-3 rounded-full shrink-0"
                                style={{
                                  backgroundColor: isStructureVisibleInTheory && categoryId != null
                                    ? getColorForCategoryId(categoryId)
                                    : '#64748b',
                                  boxShadow: isStructureVisibleInTheory ? '0 0 8px rgba(0,0,0,0.1)' : 'none'
                                }}
                              />

                              {/* Label & Badges */}
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-sm font-medium text-slate-700 dark:text-slate-200">
                                  {getQualityFlagDisplayName(flag)}
                                </span>

                                {hasAiSeg && (
                                  <span className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-purple-100 dark:bg-purple-900/40 border border-purple-200 dark:border-purple-500/30 text-purple-700 dark:text-purple-300 text-[10px] font-bold">
                                    🤖 1 AI
                                  </span>
                                )}
                              </div>
                            </div>

                            {/* THE FIXED TOGGLE SWITCH */}
                            <button
                              onClick={() => handleFlagToggle(flag)}
                              disabled={!isEditingFlags}
                              className={`w-10 h-5 rounded-full relative transition-colors flex-shrink-0 ${flag.value ? 'bg-blue-500' : 'bg-gray-300'} ${!isEditingFlags ? 'opacity-50' : 'cursor-pointer'}`}
                            >
                              <div
                                className={`w-4 h-4 rounded-full bg-white absolute top-0.5 transition-transform ${flag.value ? 'translate-x-5' : 'translate-x-0.5'}`}
                              ></div>
                            </button>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="text-center text-sm text-gray-500 pt-10">
                      {t('qualityPanel.noCriteria', {
                        defaultValue: 'No quality criteria for this frame.',
                      })}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </main>
      {selectedFrame && (
        <PixelSpacingModal
          isOpen={isCalibrationModalOpen}
          onClose={() => setIsCalibrationModalOpen(false)}
          onSave={handleSavePixelSpacing}
          frameImage={selectedFrame.originalImage}
          studyUid={studyUID}
          serieUid={selectedFrame.serie_instance_uid}
          instanceUid={selectedFrame.sop_instance_uid}
          frameIndex={Number(selectedFrame.frame_number)}
          onUpdateSuccess={({
            sop_instance_uid,
            frame_index,
            pixel_spacing,
          }: {
            sop_instance_uid: string;
            frame_index: number;
            pixel_spacing: [number, number];
          }) => {
            applyPixelSpacingToFrame(
              sop_instance_uid,
              frame_index,
              pixel_spacing[0]
            );
          }}
        />
      )}
      <ProceedConfirmationModal
        isOpen={showProceedModal}
        onClose={() => setShowProceedModal(false)}
        onProceed={executeProceeedAsView}
        viewName={view.display_name}
        frameNumber={selectedFrame?.frame_number}
        measurements={
          viewMeasKeys
            ? (selectedFrame?.measurements || []).filter(
              (m: { meas_name: string }) => viewMeasKeys.has(m.meas_name)
            )
            : []
        }
      />

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center">
                <span className="text-red-600 dark:text-red-400 text-xl">
                  ⚠️
                </span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {t('deleteFrameModal.title', { defaultValue: 'Delete Frame' })}
              </h3>
            </div>

            <p className="text-gray-600 dark:text-gray-300 mb-6">
              {t('deleteFrameModal.body', {
                defaultValue:
                  'Are you sure you want to delete this frame? This action cannot be undone and will remove all associated measurements and quality flags.',
              })}
            </p>

            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="flex-1 px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg font-medium transition-colors"
              >
                {t('actions.cancel', { defaultValue: 'Cancel' })}
              </button>
              <button
                onClick={executeDeleteFrameView}
                className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors"
              >
                {t('deleteFrameModal.confirm', {
                  defaultValue: 'Delete Frame',
                })}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Segmentation Delete Warning Modal */}
      {showSegDeleteWarning && pendingFlagToggle && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-amber-100 dark:bg-amber-900/20 rounded-full flex items-center justify-center">
                <span className="text-amber-600 dark:text-amber-400 text-xl">
                  ⚠️
                </span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {t('segmentationWarningModal.title', {
                  defaultValue: 'Delete Segmentation?',
                })}
              </h3>
            </div>

            <p className="text-gray-600 dark:text-gray-300 mb-6">
              {t('segmentationWarningModal.body', {
                defaultValue:
                  'Changing "{{label}}" to {{value}} will delete the associated segmentation drawing. This action cannot be undone.',
                label: getQualityFlagDisplayName(pendingFlagToggle),
                value: !pendingFlagToggle.value
                  ? t('common.yes', { defaultValue: 'Yes' })
                  : t('common.no', { defaultValue: 'No' }),
              })}
            </p>

            <div className="flex gap-3">
              <button
                onClick={cancelSegmentationDelete}
                className="flex-1 px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg font-medium transition-colors"
              >
                {t('actions.cancel', { defaultValue: 'Cancel' })}
              </button>
              <button
                onClick={confirmSegmentationDelete}
                className="flex-1 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg font-medium transition-colors"
              >
                {t('segmentationWarningModal.confirm', {
                  defaultValue: 'Delete & Update',
                })}
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeleteConfirmModal && pendingToggleFlag && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100]">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center">
                <AlertCircle
                  className="text-red-600 dark:text-red-400"
                  size={24}
                />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {t('removeStructureModal.title', {
                  defaultValue: 'Remove Structure?',
                })}
              </h3>
            </div>

            <p className="text-gray-600 dark:text-gray-300 mb-6">
              {t('removeStructureModal.body', {
                defaultValue:
                  'Are you sure you want to mark "{{label}}" as absent? This will permanently delete the drawing and its measurements.',
                label: getQualityFlagDisplayName(pendingToggleFlag),
              })}
            </p>

            <div className="flex gap-3">
              <button
                disabled={isDeletingStructure}
                onClick={() => {
                  setShowDeleteConfirmModal(false);
                  setPendingToggleFlag(null);
                }}
                className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors ${isDeletingStructure
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
              >
                {t('actions.cancel', { defaultValue: 'Cancel' })}
              </button>
              <button
                disabled={isDeletingStructure}
                onClick={() => executeFlagToggle(pendingToggleFlag)}
                className={`flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2 ${isDeletingStructure ? 'opacity-80 cursor-not-allowed' : ''
                  }`}
              >
                {isDeletingStructure ? (
                  <>
                    <Spin size="small" className="brightness-200" />
                    <span>
                      {t('removeStructureModal.deleting', {
                        defaultValue: 'Deleting...',
                      })}
                    </span>
                  </>
                ) : (
                  t('removeStructureModal.confirm', {
                    defaultValue: 'Confirm Delete',
                  })
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewDetails;
